import React, { useState, useEffect, useMemo } from 'react';
import { fmtDate } from '@shared/formatDate';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  Radio,
  RadioGroup,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Link,
} from '@mui/material';
import {
  Save as SaveIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  Verified as VerifiedIcon,
  Warning as WarningIcon,
  Lock as LockIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  Security as SecurityIcon,
  QrCode as QrCodeIcon,
  ContentCopy as ContentCopyIcon,
  Badge as BadgeIcon,
  SwapHoriz as SwapHorizIcon,
  Phone as PhoneIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Fingerprint as FingerprintIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import LocalArchivePanel from '../settings/LocalArchivePanel';
import CollapsibleSettingsSection from './CollapsibleSettingsSection';
import dayjs from 'dayjs';
import userAccountService, {
  UserAccountDetails,
  UpdateUserAccountRequest,
  CredentialInfo as ServiceCredentialInfo,
  CompositeAccountDetails,
  BusinessAccountInfo,
} from '../../services/userAccountService';
import registrationService, { VerificationStatus } from '../../services/registrationService';
import identityVerificationService from '../../services/identityVerificationService';
import identityDisclosureService, { type IdentityDisclosureEvidence } from '../../services/identityDisclosureService';
import { formatMailingAddressForDisplay } from '../../utils/mailingAddressFormat';
import PremiumUsernameSettings from './PremiumUsernameSettings';
import RedemptionEligibilityPanel from './RedemptionEligibilityPanel';
import SpCommissionReportsPanel from './SpCommissionReportsPanel';
import AccountClosureDialog from './AccountClosureDialog';
import NativeDisplaySettings from '../native/NativeDisplaySettings';
import NativeBiometricSettings from '../native/NativeBiometricSettings';
import { accountService } from '../../services/accountService';
import BusinessRedemptionIdentitySection from './BusinessRedemptionIdentitySection';
import apiService from '../../services/api';
import { COUNTRIES, Country, Subdivision, getSubdivisions, getSubdivisionLabel, getCountryByCode, getCountryByName } from '../../data/countries';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { abortPasskeyCeremony, runExclusivePasskeyCeremony } from '@summa/mfa-login';
import { useAuth } from '../../hooks/useAuth';
import { authService } from '../../services/authService';
import { useAccountSettingsSections } from '../../hooks/useAccountSettingsSections';
import { useSearchParams } from 'react-router-dom';
import { isStoreDistribution, isNativeShell } from '../../utils/distribution';
import { browserSupportsWebAuthn } from '../../utils/webAuthnSupport';

// ── Address verification types ────────────────────────────────────────────
interface AddressVerifyRequest {
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
}

interface AddressVerifyResponse {
  success: boolean;
  isValid: boolean;
  wasCorrected: boolean;
  message: string;
  verifiedAddress?: { street1: string; street2?: string; city: string; state: string; zip: string };
}

async function verifyAddressApi(req: AddressVerifyRequest): Promise<AddressVerifyResponse> {
  return apiService.post<AddressVerifyResponse>('/api/account/verify-address', req);
}


interface AccountSettingsProps {
  onChangePasswordClick?: () => void;
  onManageAliases?: () => void;
}

const AccountSettings: React.FC<AccountSettingsProps> = ({ onChangePasswordClick, onManageAliases }) => {
  const storeMode = isStoreDistribution();
  const nativeShell = isNativeShell();
  const webAuthnSupported = browserSupportsWebAuthn();
  const IDV_PENDING_SESSION_KEY = 'idvPendingSessionId';
  const ID_VERIFICATION_FEE = 1.45;
  const { isBusinessAccount, isMinor, user, aliasId, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const forceMfa = searchParams.get('section') === 'mfa' || searchParams.get('section') === 'two-factor-auth';
  const { isExpanded, toggleSection, expandSection } = useAccountSettingsSections({
    forceExpandSection: forceMfa ? 'two-factor-auth' : null,
  });

  useEffect(() => {
    if (forceMfa) {
      expandSection('two-factor-auth');
      const el = document.getElementById('two-factor-auth');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [forceMfa, expandSection]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [businessSaving, setBusinessSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [compositeDetails, setCompositeDetails] = useState<CompositeAccountDetails | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<UpdateUserAccountRequest>({});
  const [dateOfBirth, setDateOfBirth] = useState<string>('');
  /** Names and phone are optional at signup, so nudge (dismissibly) until they are filled in. */
  const [profileCompletionDismissed, setProfileCompletionDismissed] = useState(() => {
    try {
      return localStorage.getItem('profile_completion_nudge_dismissed') === '1';
    } catch {
      return false;
    }
  });
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [selectedSubdivision, setSelectedSubdivision] = useState<Subdivision | null>(null);

  // Email change dialog state
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [emailChangeStep, setEmailChangeStep] = useState<'enter' | 'verify'>('enter');
  const [emailChangeLoading, setEmailChangeLoading] = useState(false);
  const [emailChangeError, setEmailChangeError] = useState<string | null>(null);
  const [emailChangeSuccess, setEmailChangeSuccess] = useState<string | null>(null);

  const [closureDialogOpen, setClosureDialogOpen] = useState(false);
  const [closureBalance, setClosureBalance] = useState(0);

  useEffect(() => {
    if (!closureDialogOpen) return;
    accountService.getBalance()
      .then((b) => setClosureBalance(b.availableBalance ?? 0))
      .catch(() => setClosureBalance(0));
  }, [closureDialogOpen]);

  // Business email change dialog state
  const [businessEmailDialogOpen, setBusinessEmailDialogOpen] = useState(false);
  const [businessNewEmail, setBusinessNewEmail] = useState('');
  const [businessEmailVerificationCode, setBusinessEmailVerificationCode] = useState('');
  const [businessEmailChangeStep, setBusinessEmailChangeStep] = useState<'enter' | 'verify'>('enter');
  const [businessEmailChangeLoading, setBusinessEmailChangeLoading] = useState(false);
  const [businessEmailChangeError, setBusinessEmailChangeError] = useState<string | null>(null);
  const [businessEmailChangeSuccess, setBusinessEmailChangeSuccess] = useState<string | null>(null);

  // 2FA state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [twoFactorPendingSetup, setTwoFactorPendingSetup] = useState(false);
  const [twoFactorLoading, setTwoFactorLoading] = useState(false);
  const [twoFactorDialogOpen, setTwoFactorDialogOpen] = useState(false);
  const [qrCodeData, setQrCodeData] = useState<string | null>(null);
  const [manualKey, setManualKey] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [totpVerificationCode, setTotpVerificationCode] = useState('');
  const [manualKeyCopied, setManualKeyCopied] = useState(false);
  const [twoFactorError, setTwoFactorError] = useState<string | null>(null);
  const [twoFactorSuccess, setTwoFactorSuccess] = useState<string | null>(null);
  const [disableDialogOpen, setDisableDialogOpen] = useState(false);
  const [disableCode, setDisableCode] = useState('');

  // Passkey state
  const [passkeyCount, setPasskeyCount] = useState(0);
  const [passkeyLoading, setPasskeyLoading] = useState(false);
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  const [passkeySuccess, setPasskeySuccess] = useState<string | null>(null);
  const [removePasskeysDialogOpen, setRemovePasskeysDialogOpen] = useState(false);
  const [removePasskeysCode, setRemovePasskeysCode] = useState('');
  const [removeOneCode, setRemoveOneCode] = useState('');
  const [removeOneTargetId, setRemoveOneTargetId] = useState<string | null>(null);
  const [registerPasskeyDialogOpen, setRegisterPasskeyDialogOpen] = useState(false);
  const [platformAuthenticatorAvailable, setPlatformAuthenticatorAvailable] = useState<boolean | null>(null);
  /** Browser/PC proven unable to create device-bound Windows Hello (Change never offers This Windows device). */
  const [thisPcHelloUnavailable, setThisPcHelloUnavailable] = useState(() => {
    try {
      return localStorage.getItem('passkey_this_pc_hello_unavailable') === '1';
    } catch {
      return false;
    }
  });

  const markThisPcHelloUnavailable = () => {
    setThisPcHelloUnavailable(true);
    try {
      localStorage.setItem('passkey_this_pc_hello_unavailable', '1');
    } catch {
      /* ignore */
    }
  };
  const [passkeysListDialogOpen, setPasskeysListDialogOpen] = useState(false);
  const [passkeysList, setPasskeysList] = useState<
    Array<{
      passkeyId: string;
      label: string;
      type: string;
      transports?: string;
      createdUtc: string;
      lastUsedUtc?: string | null;
    }>
  >([]);
  const [passkeysListLoading, setPasskeysListLoading] = useState(false);
  const [allowMfaEmailRecovery, setAllowMfaEmailRecovery] = useState(false);
  const [requireMfaForMicropayLinks, setRequireMfaForMicropayLinks] = useState(false);
  const [preferredMfaMethod, setPreferredMfaMethod] = useState<'passkey' | 'totp' | ''>('');
  const [mfaSettingsLoading, setMfaSettingsLoading] = useState(false);
  const [accountRecoveryCodeCount, setAccountRecoveryCodeCount] = useState(0);
  const [passkeyRecoveryCodes, setPasskeyRecoveryCodes] = useState<string[]>([]);
  const [mfaSettingsNote, setMfaSettingsNote] = useState<string | null>(null);

  // Credential management state
  type CredentialInfo = ServiceCredentialInfo;
  const [credentials, setCredentials] = useState<CredentialInfo[]>([]);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [availableUsernames, setAvailableUsernames] = useState<string[]>([]);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedCredential, setSelectedCredential] = useState<CredentialInfo | null>(null);
  const [transferTargetUsername, setTransferTargetUsername] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [credentialError, setCredentialError] = useState<string | null>(null);
  const [credentialSuccess, setCredentialSuccess] = useState<string | null>(null);

  // Per-credential profile editing state: { [credentialId]: { businessName, fein, websiteUrl } }
  const [credentialEdits, setCredentialEdits] = useState<Record<string, { businessName: string; fein: string; websiteUrl: string }>>({});
  const [credentialSaving, setCredentialSaving] = useState<Record<string, boolean>>({});
  const [businessProfileEdits, setBusinessProfileEdits] = useState({
    businessName: '',
    dbaName: '',
    fein: '',
    websiteUrl: '',
    contactName: '',
    phoneNumber: '',
    email: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
  });

  // Verification state
  const [verificationStatus, setVerificationStatus] = useState<VerificationStatus | null>(null);
  const [idVerificationStarting, setIdVerificationStarting] = useState(false);
  const [pendingIdvSessionId, setPendingIdvSessionId] = useState<string | null>(null);
  const [idVerificationConfirmOpen, setIdVerificationConfirmOpen] = useState(false);
  const [idPreviewOpen, setIdPreviewOpen] = useState(false);
  const [idPreviewLoading, setIdPreviewLoading] = useState(false);
  const [idPreviewError, setIdPreviewError] = useState<string | null>(null);
  const [idPreviewEvidence, setIdPreviewEvidence] = useState<IdentityDisclosureEvidence | null>(null);
  const [autoApproveAllRequests, setAutoApproveAllRequests] = useState(true);
  const [autoApproveLoading, setAutoApproveLoading] = useState(false);
  const [autoApproveSaving, setAutoApproveSaving] = useState(false);

  // Phone verification dialog
  const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);
  const [phoneValue, setPhoneValue] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneCodeSent, setPhoneCodeSent] = useState(false);
  const [phoneSending, setPhoneSending] = useState(false);
  const [phoneVerifying, setPhoneVerifying] = useState(false);
  const [phoneVerifyError, setPhoneVerifyError] = useState<string | null>(null);
  const [phoneVerifyTarget, setPhoneVerifyTarget] = useState<'personal' | 'business'>('personal');
  const [savedPhoneNumber, setSavedPhoneNumber] = useState<string>('');
  const [savedBusinessProfile, setSavedBusinessProfile] = useState({
    phoneNumber: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zipCode: '',
  });

  // Address verification dialog
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [addressVerifyTarget, setAddressVerifyTarget] = useState<'personal' | 'business'>('personal');
  const [addressForm, setAddressForm] = useState({ street1: '', street2: '', city: '', state: '', zip: '' });
  const [addressSubmitting, setAddressSubmitting] = useState(false);
  const [addressResult, setAddressResult] = useState<AddressVerifyResponse | null>(null);
  const [addressVerifyError, setAddressVerifyError] = useState<string | null>(null);

  // Memoized subdivisions based on selected country
  const subdivisions = useMemo(() => {
    if (!selectedCountry) return [];
    return getSubdivisions(selectedCountry.code);
  }, [selectedCountry]);

  const subdivisionLabel = useMemo(() => {
    if (!selectedCountry) return 'State/Province';
    return getSubdivisionLabel(selectedCountry.code);
  }, [selectedCountry]);

  const personalAccount = compositeDetails?.personalAccount ?? null;
  const showBusinessSection = compositeDetails?.showBusinessSection ?? false;
  const canEditBusinessInfo = compositeDetails?.canEditBusinessInfo ?? false;
  const businessAccount = compositeDetails?.businessAccount ?? null;
  const aliasContext = compositeDetails?.aliasContext ?? null;

  const primaryBusinessCredential = useMemo(() => (
    credentials.find(c => c.credentialType === 'BUSINESS') ||
    credentials.find(c => c.isBusinessAccount) ||
    null
  ), [credentials]);

  const isBusinessProfile = useMemo(() => (
    showBusinessSection ||
    isBusinessAccount ||
    !!personalAccount?.isBusinessAccount ||
    credentials.some(c => c.isBusinessAccount)
  ), [showBusinessSection, isBusinessAccount, personalAccount, credentials]);

  // Cash redemption on a personal login stays personal even when this user
  // also holds business credentials or team invitations.
  const businessJointSession = useMemo(() => (
    !!aliasContext?.isJointContext
    && (aliasContext.jointAccountType ?? '').toUpperCase() === 'BUSINESS'
  ), [aliasContext]);

  const shouldReconcileIdvOnReturn = useMemo(() => {
    if (import.meta.env.DEV) {
      return true;
    }

    const host = window.location.hostname.toLowerCase();
    return (
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host === '::1' ||
      host === 'mail.qixit.com' ||
      host === 'stmail.qixit.com'
    );
  }, []);

  const previewMailingAddress = useMemo(() => {
    if (!idPreviewEvidence) return '';
    return formatMailingAddressForDisplay(idPreviewEvidence);
  }, [idPreviewEvidence]);

  const idVerificationInlineError = useMemo(() => {
    if (!error) return null;
    const lower = error.toLowerCase();
    if (
      lower.includes('identity verification') ||
      lower.includes('re-verify identity') ||
      lower.includes('didit') ||
      lower.includes('date of birth is required before id verification') ||
      lower.includes('session not found')
    ) {
      return error;
    }
    return null;
  }, [error]);

  const clearStaleIdvSession = () => {
    localStorage.removeItem(IDV_PENDING_SESSION_KEY);
    setPendingIdvSessionId(null);
  };

  const isStaleIdvSessionError = (err: unknown) => {
    const response = (err as { response?: { status?: number; data?: { message?: string } } })?.response;
    const message = (response?.data?.message || (err as Error)?.message || '').toLowerCase();
    return response?.status === 404 || message.includes('session not found');
  };

  useEffect(() => {
    const pending = localStorage.getItem(IDV_PENDING_SESSION_KEY);
    if (pending) {
      setPendingIdvSessionId(pending);
    }
  }, []);

  useEffect(() => {
    fetchAccountDetails();
    if (!isMinor) {
      fetchTwoFactorStatus();
      fetchCredentials();
      fetchPasskeyStatus();
      fetchMfaSettings();
      handleIdentityVerificationReturn();
      loadIdentityDisclosurePreferences();
    }
    fetchVerificationStatus();
  }, [isMinor]);

  useEffect(() => {
    if (isMinor || !pendingIdvSessionId) return;

    let cancelled = false;
    const terminalStatuses = new Set(['VERIFIED', 'FAILED', 'EXPIRED', 'CANCELLED']);

    const poll = async () => {
      try {
        const session = shouldReconcileIdvOnReturn
          ? (await identityVerificationService.reconcileCallback(pendingIdvSessionId)).session
          : await identityVerificationService.getSession(pendingIdvSessionId);
        if (!session) {
          return;
        }
        const status = (session.status || '').toUpperCase();
        if (!terminalStatuses.has(status) || cancelled) {
          return;
        }

        localStorage.removeItem(IDV_PENDING_SESSION_KEY);
        setPendingIdvSessionId(null);
        await fetchVerificationStatus();

        if (status === 'VERIFIED') {
          setSuccess('Identity verification completed successfully.');
        } else {
          setError(`Identity verification finished with status: ${session.status}.`);
        }
      } catch (err) {
        if (isStaleIdvSessionError(err)) {
          clearStaleIdvSession();
          return;
        }
        // Session may not be visible yet or callback is still in flight.
      }
    };

    poll();
    const timer = window.setInterval(poll, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [pendingIdvSessionId, shouldReconcileIdvOnReturn]);

  const finalizeIdentityVerificationSession = async (
    sessionId: string,
    callbackStatus?: string | null
  ) => {
    try {
      const result = shouldReconcileIdvOnReturn
        ? await identityVerificationService.reconcileCallback(sessionId, callbackStatus ?? undefined)
        : null;
      const session = result?.session ?? await identityVerificationService.getSession(sessionId);
      const status = (session?.status || '').toUpperCase();
      const terminalStatuses = new Set(['VERIFIED', 'FAILED', 'EXPIRED', 'CANCELLED']);

      if (terminalStatuses.has(status)) {
        localStorage.removeItem(IDV_PENDING_SESSION_KEY);
        setPendingIdvSessionId(null);
        await fetchVerificationStatus();
        if (status === 'VERIFIED') {
          setSuccess('Identity verification completed successfully.');
        } else if (callbackStatus) {
          setSuccess('Identity verification callback was received.');
        }
      }
    } catch (err: unknown) {
      if (isStaleIdvSessionError(err)) {
        clearStaleIdvSession();
        return;
      }
      const message =
        (err as { response?: { data?: { message?: string; error?: string } } })?.response?.data?.message ||
        (err as { response?: { data?: { message?: string; error?: string } } })?.response?.data?.error ||
        (err as Error)?.message ||
        'Identity verification callback could not be reconciled.';
      setError(message);
    }
  };

  const handleIdentityVerificationReturn = async () => {
    const url = new URL(window.location.href);
    const searchParams = url.searchParams;

    const rawHash = url.hash || '';
    const hashQueryIndex = rawHash.indexOf('?');
    const hashPath = hashQueryIndex >= 0 ? rawHash.slice(0, hashQueryIndex) : rawHash;
    const hashParams = new URLSearchParams(hashQueryIndex >= 0 ? rawHash.slice(hashQueryIndex + 1) : '');

    const sessionId =
      searchParams.get('verificationSessionId') ||
      hashParams.get('verificationSessionId') ||
      searchParams.get('vendor_data') ||
      hashParams.get('vendor_data') ||
      localStorage.getItem(IDV_PENDING_SESSION_KEY);
    const callbackStatus = searchParams.get('status') || hashParams.get('status');
    if (!sessionId) {
      return;
    }

    const clearCallbackParams = () => {
      searchParams.delete('verificationSessionId');
      searchParams.delete('status');
      searchParams.delete('vendor_data');
      hashParams.delete('verificationSessionId');
      hashParams.delete('status');
      hashParams.delete('vendor_data');
      const nextHash = `${hashPath}${hashParams.toString() ? `?${hashParams.toString()}` : ''}`;
      const next = `${url.pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ''}${nextHash}`;
      window.history.replaceState({}, '', next);
    };

    localStorage.setItem(IDV_PENDING_SESSION_KEY, sessionId);
    setPendingIdvSessionId(sessionId);

    await finalizeIdentityVerificationSession(sessionId, callbackStatus);
    clearCallbackParams();
  };

  const fetchVerificationStatus = async () => {
    if (isMinor) {
      setVerificationStatus({
        userId: aliasId || '',
        username: user?.username || '',
        verificationLevel: 0,
        verificationLevelName: 'None',
        isEmailVerified: false,
        isPhoneVerified: false,
        isIdVerified: false,
        isAddressVerified: false,
      });
      return;
    }

    try {
      const data = await registrationService.getVerificationStatus();
      setVerificationStatus(data);
    } catch (err) {
      console.error('Failed to fetch verification status:', err);
    }
  };

  // ── Phone verification handlers ──────────────────────────────────────────
  const handleOpenPhoneDialog = () => {
    setPhoneVerifyTarget('personal');
    setPhoneValue((formData.phoneNumber || '').trim() || savedPhoneNumber.trim());
    setPhoneCode('');
    setPhoneCodeSent(false);
    setPhoneVerifyError(null);
    setPhoneDialogOpen(true);
  };

  const handleOpenBusinessPhoneDialog = () => {
    setPhoneVerifyTarget('business');
    setPhoneValue((businessProfileEdits.phoneNumber || '').trim());
    setPhoneCode('');
    setPhoneCodeSent(false);
    setPhoneVerifyError(null);
    setPhoneDialogOpen(true);
  };

  const handleSendPhoneCode = async () => {
    setPhoneSending(true);
    setPhoneVerifyError(null);
    try {
      let message = 'Verification code sent.';
      if (phoneVerifyTarget === 'business' && businessAccount?.credentialId) {
        const resp = await userAccountService.requestBusinessPhoneVerification(businessAccount.credentialId, phoneValue);
        message = resp.message || message;
      } else {
        const resp = await registrationService.requestPhoneVerification(phoneValue);
        message = resp.message || message;
      }
      setPhoneCodeSent(true);
      setSuccess(message);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to send verification code';
      setPhoneVerifyError(msg);
    } finally {
      setPhoneSending(false);
    }
  };

  const handleVerifyPhoneCode = async () => {
    setPhoneVerifying(true);
    setPhoneVerifyError(null);
    try {
      if (phoneVerifyTarget === 'business' && businessAccount?.credentialId) {
        await userAccountService.confirmBusinessPhoneVerification(businessAccount.credentialId, phoneCode);
        setPhoneDialogOpen(false);
        setSuccess('Business phone number verified successfully!');
        await fetchAccountDetails();
      } else {
        await registrationService.submitVerificationCode({ verificationType: 'PHONE', code: phoneCode });
        setPhoneDialogOpen(false);
        setSuccess('Phone number verified successfully!');
        await fetchAccountDetails();
        await fetchVerificationStatus();
      }
    } catch (err: any) {
      setPhoneVerifyError(err.response?.data?.message || 'Verification failed. Please check the code and try again.');
    } finally {
      setPhoneVerifying(false);
    }
  };

  // ── Address verification handlers ────────────────────────────────────────
  const handleOpenAddressDialog = () => {
    setAddressVerifyTarget('personal');
    setAddressForm({
      street1: formData.address1 || '',
      street2: formData.address2 || '',
      city: formData.city || '',
      state: formData.state || '',
      zip: formData.zipCode || '',
    });
    setAddressResult(null);
    setAddressVerifyError(null);
    setAddressDialogOpen(true);
  };

  const handleOpenBusinessAddressDialog = () => {
    setAddressVerifyTarget('business');
    setAddressForm({
      street1: businessProfileEdits.address1 || '',
      street2: businessProfileEdits.address2 || '',
      city: businessProfileEdits.city || '',
      state: businessProfileEdits.state || '',
      zip: businessProfileEdits.zipCode || '',
    });
    setAddressResult(null);
    setAddressVerifyError(null);
    setAddressDialogOpen(true);
  };

  const handleAddressSubmit = async () => {
    if (!addressForm.street1 || !addressForm.city || !addressForm.state || !addressForm.zip) {
      setAddressVerifyError('Street, city, state, and ZIP are required.');
      return;
    }
    setAddressSubmitting(true);
    setAddressVerifyError(null);
    try {
      if (addressVerifyTarget === 'business' && businessAccount?.credentialId) {
        const resp = await userAccountService.verifyBusinessAddress(businessAccount.credentialId, addressForm);
        if (resp.success && resp.isValid) {
          setAddressDialogOpen(false);
          setSuccess(resp.message || 'Business address verified successfully!');
          await fetchAccountDetails();
        } else {
          setAddressVerifyError(resp.message || 'Address could not be verified.');
        }
      } else {
        const resp = await verifyAddressApi(addressForm);
        if (resp.success && resp.isValid) {
          setAddressResult(resp);
        } else {
          setAddressVerifyError(resp.message || 'Address could not be verified.');
        }
      }
    } catch (err: any) {
      setAddressVerifyError(err.response?.data?.message || err.message || 'Address verification failed.');
    } finally {
      setAddressSubmitting(false);
    }
  };

  const handleAddressConfirm = () => {
    setAddressDialogOpen(false);
    setSuccess('Address verified! Your mailing address has been confirmed.');
    fetchAccountDetails();
    fetchVerificationStatus();
  };

  const handleStartIdentityVerification = async () => {
    if (!dateOfBirth) {
      setError(
        'Date of birth is required before ID verification. Please enter Date of Birth in Account Settings and click Save, then try Re-verify Identity again.'
      );
      return;
    }

    setIdVerificationStarting(true);
    setError(null);
    setSuccess(null);

    try {
      const normalizedDob = dateOfBirth.trim();
      const persistedDob = personalAccount?.dateOfBirth
        ? dayjs(personalAccount.dateOfBirth).format('YYYY-MM-DD')
        : '';

      // Ensure DOB is persisted before launching provider flow.
      if (normalizedDob && normalizedDob !== persistedDob) {
        await userAccountService.updateAccountDetails({ dateOfBirth: normalizedDob });
        await fetchAccountDetails();
      }

      const host = window.location.hostname.toLowerCase();
      const useHashRouting = host === 'mail.qixit.com' || host === 'stmail.qixit.com';
      const returnUrl = useHashRouting
        ? `${window.location.origin}/login#/profile?tab=account-settings`
        : `${window.location.origin}/profile?tab=account-settings`;
      const result = await identityVerificationService.startSession({ returnUrl });
      const launchUrl = result.session?.launchUrl;
      const sessionId = result.session?.sessionId;

      if (!launchUrl) {
        throw new Error('Identity verification session started, but no launch URL was returned.');
      }
      if (sessionId) {
        localStorage.setItem(IDV_PENDING_SESSION_KEY, sessionId);
        setPendingIdvSessionId(sessionId);
      }

      setSuccess('Opening identity verification...');
      window.location.assign(launchUrl);
    } catch (err: any) {
      const statusCode = err?.response?.status;
      const responseData = err?.response?.data as {
        requiredFee?: number;
        currentBalance?: number;
        message?: string;
      } | undefined;
      const requiredMatch = String(err?.response?.data?.message || '').match(/Required:\s*\$([0-9]+(?:\.[0-9]{1,2})?)/i);
      const availableMatch = String(err?.response?.data?.message || '').match(/Available:\s*\$([0-9]+(?:\.[0-9]{1,2})?)/i);

      if (statusCode === 402) {
        const requiredFee = responseData?.requiredFee != null
          ? Number(responseData.requiredFee).toFixed(2)
          : (requiredMatch?.[1] ?? '1.45');
        const availableBalance = responseData?.currentBalance != null
          ? Number(responseData.currentBalance).toFixed(2)
          : (availableMatch?.[1] ?? '0.00');
        const nextStepMessage = storeMode
          ? 'Please manage your account on the Qixit website, then try Re-verify Identity again.'
          : 'Please add funds from Account History, then try Re-verify Identity again.';
        setError(
          `Identity verification requires at least $${requiredFee} in available balance. ` +
          `Your current available balance is $${availableBalance}. ` +
          nextStepMessage
        );
        return;
      }

      const rawMessage =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        'Unable to start identity verification right now.';
      const lowerMessage = String(rawMessage).toLowerCase();
      if (lowerMessage.includes('not configured') || lowerMessage.includes('didit')) {
        setError(
          `${rawMessage} If this persists in staging, Didit API settings for the environment may be missing or invalid.`
        );
      } else {
        setError(rawMessage);
      }
    } finally {
      setIdVerificationStarting(false);
    }
  };

  const handleOpenIdentityVerificationConfirm = () => {
    setError(null);
    setSuccess(null);
    setIdVerificationConfirmOpen(true);
  };

  const handleOpenIdentityPreview = async () => {
    setIdPreviewOpen(true);
    setIdPreviewLoading(true);
    setIdPreviewError(null);
    setIdPreviewEvidence(null);
    try {
      const data = await identityDisclosureService.getMyVerificationPreview();
      setIdPreviewEvidence(data);
    } catch (err: any) {
      const message =
        err?.response?.status === 404
          ? 'No verification evidence found yet. Complete identity verification first.'
          : err?.message === 'Network Error'
            ? 'Could not reach the mailbox API to load your verification preview. Please retry in a moment.'
            : (err?.response?.data?.message || err?.message || 'Failed to load verification preview.');
      setIdPreviewError(message);
    } finally {
      setIdPreviewLoading(false);
    }
  };

  const loadIdentityDisclosurePreferences = async () => {
    try {
      setAutoApproveLoading(true);
      const pref = await identityDisclosureService.getPreferences();
      setAutoApproveAllRequests(Boolean(pref.autoApproveAllRequests));
    } catch {
      // Keep default-on UI fallback when preference has not been set yet.
      setAutoApproveAllRequests(true);
    } finally {
      setAutoApproveLoading(false);
    }
  };

  const handleAutoApproveToggle = async (enabled: boolean) => {
    const previous = autoApproveAllRequests;
    setAutoApproveAllRequests(enabled);
    setAutoApproveSaving(true);
    try {
      const updated = await identityDisclosureService.updatePreferences({ autoApproveAllRequests: enabled });
      setAutoApproveAllRequests(Boolean(updated.autoApproveAllRequests));
      setSuccess(
        Boolean(updated.autoApproveAllRequests)
          ? 'Auto-approve is ON for identity verification sharing (mailing addresses still require explicit approval).'
          : 'Auto-approve is OFF. You will review each identity verification request manually.'
      );
    } catch (err: any) {
      setAutoApproveAllRequests(previous);
      const message = err?.response?.data?.message || err?.message || 'Failed to update auto-approve preference.';
      setError(message);
    } finally {
      setAutoApproveSaving(false);
    }
  };

  const fetchCredentials = async () => {
    try {
      setCredentialsLoading(true);
      const response = await userAccountService.getCredentials();
      const creds = response.credentials || [];
      setCredentials(creds);
      setAvailableUsernames(response.availableUsernames || []);
      const edits: Record<string, { businessName: string; fein: string; websiteUrl: string }> = {};
      for (const c of creds) {
        edits[c.credentialId] = {
          businessName: c.businessName || '',
          fein: c.fein || '',
          websiteUrl: c.websiteUrl || '',
        };
      }
      setCredentialEdits(edits);
      const businessCredential = creds.find(c => c.credentialType === 'BUSINESS') || creds.find(c => c.isBusinessAccount);
      if (businessCredential) {
        setBusinessProfileEdits(prev => ({
          ...prev,
          businessName: prev.businessName || businessCredential.businessName || '',
          dbaName: prev.dbaName || '',
          fein: prev.fein || businessCredential.fein || '',
          websiteUrl: prev.websiteUrl || businessCredential.websiteUrl || '',
        }));
      }
    } catch (err) {
      console.error('Failed to fetch credentials:', err);
    } finally {
      setCredentialsLoading(false);
    }
  };

  const handleOpenTransferDialog = (credential: CredentialInfo) => {
    setSelectedCredential(credential);
    setTransferTargetUsername('');
    setCredentialError(null);
    setTransferDialogOpen(true);
  };

  const handleTransferCredential = async () => {
    if (!selectedCredential || !transferTargetUsername) return;
    
    try {
      setTransferLoading(true);
      setCredentialError(null);
      
      const result = await userAccountService.transferCredential(
        selectedCredential.credentialId,
        transferTargetUsername
      );
      
      if (result.success) {
        setCredentialSuccess(`Credential transferred to ${transferTargetUsername}`);
        setTransferDialogOpen(false);
        await fetchCredentials();
      } else {
        throw new Error(result.message || 'Transfer failed');
      }
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to transfer credential';
      setCredentialError(message);
    } finally {
      setTransferLoading(false);
    }
  };

  const handleSaveCredentialProfile = async (credId: string) => {
    const edits = credentialEdits[credId];
    if (!edits) return;

    setCredentialSaving(prev => ({ ...prev, [credId]: true }));
    setCredentialError(null);

    try {
      const result = await userAccountService.updateCredentialProfile(credId, {
        businessName: edits.businessName || undefined,
        fein: edits.fein || undefined,
        websiteUrl: edits.websiteUrl || undefined,
      });
      if (result.success) {
        setCredentialSuccess(result.message || 'Credential profile updated.');
        await fetchCredentials();
      } else {
        setCredentialError(result.message || 'Update failed.');
      }
    } catch (err: any) {
      setCredentialError(err.response?.data?.message || err.message || 'Failed to update credential profile.');
    } finally {
      setCredentialSaving(prev => ({ ...prev, [credId]: false }));
    }
  };

  const handleCredentialFieldChange = (credId: string, field: 'businessName' | 'fein' | 'websiteUrl', value: string) => {
    setCredentialEdits(prev => ({
      ...prev,
      [credId]: { ...prev[credId], [field]: value },
    }));
  };

  const isCredentialProfileDirty = (cred: CredentialInfo): boolean => {
    const edits = credentialEdits[cred.credentialId];
    if (!edits) return false;
    return (edits.businessName !== (cred.businessName || ''))
      || (edits.fein !== (cred.fein || ''))
      || (edits.websiteUrl !== (cred.websiteUrl || ''));
  };

  const handleBusinessProfileFieldChange = (
    field: keyof typeof businessProfileEdits,
    value: string
  ) => {
    setBusinessProfileEdits(prev => ({ ...prev, [field]: value }));
  };

  const isBusinessProfileDirty = () => {
    if (!businessAccount) return false;
    return (
      businessProfileEdits.businessName !== (businessAccount.businessName || '') ||
      businessProfileEdits.fein !== (businessAccount.fein || '') ||
      businessProfileEdits.websiteUrl !== (businessAccount.websiteUrl || '') ||
      businessProfileEdits.contactName !== (businessAccount.contactName || '') ||
      businessProfileEdits.phoneNumber !== (businessAccount.phoneNumber || '') ||
      businessProfileEdits.address1 !== (businessAccount.address1 || '') ||
      businessProfileEdits.address2 !== (businessAccount.address2 || '') ||
      businessProfileEdits.city !== (businessAccount.city || '') ||
      businessProfileEdits.state !== (businessAccount.state || '') ||
      businessProfileEdits.zipCode !== (businessAccount.zipCode || '') ||
      businessProfileEdits.country !== (businessAccount.country || '')
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const phoneChanged = (formData.phoneNumber || '').trim() !== savedPhoneNumber.trim();
      const { email, ...updateWithoutEmail } = formData;
      const updateRequest: UpdateUserAccountRequest = {
        ...updateWithoutEmail,
        dateOfBirth: dateOfBirth || undefined,
      };

      const result = await userAccountService.updateAccountDetails(updateRequest);
      setSuccess(result.message);
      await fetchAccountDetails();

      if (phoneChanged && formData.phoneNumber) {
        setPhoneValue(formData.phoneNumber);
        setPhoneCode('');
        setPhoneCodeSent(false);
        setPhoneVerifyError(null);
        setPhoneDialogOpen(true);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save account details');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBusinessInfo = async () => {
    const credentialId = businessAccount?.credentialId;
    if (!credentialId || !canEditBusinessInfo) return;

    try {
      setBusinessSaving(true);
      setError(null);
      setSuccess(null);

      await userAccountService.updateCredentialProfile(credentialId, {
        businessName: businessProfileEdits.businessName || undefined,
        fein: businessProfileEdits.fein || undefined,
        websiteUrl: businessProfileEdits.websiteUrl || undefined,
        contactName: businessProfileEdits.contactName || undefined,
        phoneNumber: businessProfileEdits.phoneNumber || undefined,
        address1: businessProfileEdits.address1 || undefined,
        address2: businessProfileEdits.address2 || undefined,
        city: businessProfileEdits.city || undefined,
        state: businessProfileEdits.state || undefined,
        zipCode: businessProfileEdits.zipCode || undefined,
        country: businessProfileEdits.country || undefined,
      });

      setSuccess('Business information updated successfully.');
      await fetchAccountDetails();
    } catch (err: any) {
      setError(err.message || 'Failed to save business information');
    } finally {
      setBusinessSaving(false);
    }
  };

  const fetchTwoFactorStatus = async () => {
    try {
      const response = await userAccountService.getTwoFactorStatus();
      setTwoFactorEnabled(response.isEnabled);
      setTwoFactorPendingSetup(!!response.isPendingSetup && !response.isEnabled);
    } catch (err) {
      console.error('Failed to fetch 2FA status:', err);
    }
  };

  type PasskeyListItem = {
    passkeyId: string;
    label: string;
    type: string;
    transports?: string;
    createdUtc: string;
    lastUsedUtc?: string | null;
  };

  const fetchPasskeyStatus = async () => {
    try {
      const data = await apiService.get<{ hasPasskeys: boolean; count: number }>('/api/passkey/status');
      setPasskeyCount(data.count);
    } catch (err) {
      console.error('Failed to fetch passkey status:', err);
    }
    // Prefetch list so Add-passkey guidance (this-computer / phone notes) is ready.
    try {
      const listData = await apiService.get<{ count: number; passkeys: PasskeyListItem[] }>('/api/passkey/list');
      setPasskeysList(listData.passkeys || []);
      if (typeof listData.count === 'number') setPasskeyCount(listData.count);
    } catch {
      /* list is optional for the status chip */
    }
  };

  /** Legacy rows without chrDeviceName: GPM hybrid cannot tell phone QR vs Chrome desktop. */
  const isAmbiguousPasskeyLabel = (p: { type?: string; label?: string }) => {
    const label = (p.label || '').toLowerCase();
    return label.includes('phone or computer') || label === 'this device or phone';
  };

  /** Phone QR enrollments — Type may still say "Google Password Manager" while Label is Phone (...). */
  const isPhoneEnrolledPasskey = (p: { type?: string; label?: string }) => {
    if (isAmbiguousPasskeyLabel(p)) return false;
    const type = (p.type || '').toLowerCase();
    const label = (p.label || '').toLowerCase();
    return (
      type.includes('phone / hybrid') ||
      type === 'phone' ||
      label.startsWith('phone (') ||
      (/\bphone\b/.test(label) && !label.includes('windows hello') && !label.includes('or computer'))
    );
  };

  /** Soft guidance for Add-passkey — never lock out other browsers by treating PM passkeys as sole MFA. */
  const analyzePasskeyAddOptions = (
    passkeys: PasskeyListItem[],
    opts?: { thisPcHelloUnavailable?: boolean; platformAuthenticatorAvailable?: boolean | null }
  ) => {
    // Phone GPM keys share the Google AAGUID Type — do not treat clear Phone (...) as PC/synced PM.
    // Ambiguous legacy GPM ("phone or computer") still blocks another This computer enrollment.
    const computerPasskeys = passkeys.filter(
      (p) => !isPhoneEnrolledPasskey(p) || isAmbiguousPasskeyLabel(p)
    );
    const types = computerPasskeys.map((p) => (p.type || '').toLowerCase());
    const labels = computerPasskeys.map((p) => (p.label || '').toLowerCase());

    const hasWindowsHello = types.some(
      (t) => t.includes('windows hello') || t.includes('this device (platform)')
    );
    const hasMicrosoftPm =
      types.some((t) => t.includes('microsoft password manager')) ||
      labels.some((l) => l.includes('microsoft password manager'));
    // Synced GPM on this computer only (exclude Phone (Google Password Manager)).
    const hasGooglePm =
      types.some((t) => t.includes('google password manager')) ||
      labels.some((l) => l.includes('google password manager'));
    const hasPhoneLike = passkeys.some((p) => isPhoneEnrolledPasskey(p));
    const hasSecurityKey = passkeys.some((p) => {
      const t = (p.type || '').toLowerCase();
      return t.includes('security key') || t.includes('usb') || t.includes('cross-platform');
    });

    const hasThisDeviceGeneric = types.some(
      (t) =>
        t.includes('this device') ||
        t.includes('platform') ||
        t.includes('windows hello')
    );
    const hasComputerBoundPasskey =
      hasWindowsHello || hasMicrosoftPm || hasGooglePm || hasThisDeviceGeneric;

    const existingSummary = passkeys.map((p) => p.type || p.label).filter(Boolean);
    const helloUnavailable = !!opts?.thisPcHelloUnavailable;
    const noPlatformAuth = opts?.platformAuthenticatorAvailable === false;

    // Split "This computer" into Hello vs password-manager — one OS create dialog that lists
    // Hello + Google + Microsoft together often conflicts (first PIN fails / wrong provider).
    // WebAuthn cannot force Google vs Microsoft separately; those share the password-manager path.
    let windowsHelloBlockedBy: string | null = null;
    if (hasWindowsHello || hasThisDeviceGeneric) {
      windowsHelloBlockedBy =
        'A Windows Hello / this-device passkey is already registered. Remove it first to replace it.';
    } else if (helloUnavailable || noPlatformAuth) {
      windowsHelloBlockedBy =
        'Windows Hello is unavailable in this browser. Use Password manager, Phone, or a security key instead.';
    }

    let passwordManagerBlockedBy: string | null = null;
    if (hasMicrosoftPm || hasGooglePm) {
      passwordManagerBlockedBy =
        'A Google or Microsoft password-manager passkey is already registered. Remove it first before adding another — duplicates often conflict across browsers.';
    }

    const windowsHelloNote = windowsHelloBlockedBy
      ? windowsHelloBlockedBy
      : 'Device-bound to this PC (fingerprint / PIN). Works in any browser on this machine. Prefer this over password managers when Hello is available.';

    const passwordManagerNote = passwordManagerBlockedBy
      ? passwordManagerBlockedBy
      : 'Saves in Google or Microsoft Password Manager (OS chooses which). May only work in browsers signed into that manager — keep an authenticator app as backup.';

    return {
      existingSummary,
      hasMicrosoftPm,
      hasGooglePm,
      hasComputerBoundPasskey,
      /** @deprecated use windowsHello / passwordManager — kept for any leftover callers */
      thisComputer: {
        available: !windowsHelloBlockedBy || !passwordManagerBlockedBy,
        blockedBy: windowsHelloBlockedBy && passwordManagerBlockedBy
          ? 'A this-computer passkey is already registered. Remove it first, or add a phone / security key.'
          : null,
        note: 'Choose Windows Hello or Password manager below.',
      },
      windowsHello: {
        available: !windowsHelloBlockedBy,
        blockedBy: windowsHelloBlockedBy,
        note: windowsHelloNote,
      },
      passwordManager: {
        available: !passwordManagerBlockedBy,
        blockedBy: passwordManagerBlockedBy,
        note: passwordManagerNote,
      },
      hybrid: {
        // Allow multiple phones; same phone/account may still be rejected by the authenticator.
        available: true,
        blockedBy: null as string | null,
        note: hasPhoneLike
          ? 'A phone passkey is already registered. You can add a different phone; the same phone/account may be rejected.'
          : 'Scan the QR code on a phone or tablet. Do not use this PC’s fingerprint/PIN for this option.',
      },
      securityKey: {
        available: true,
        blockedBy: null as string | null,
        note: hasSecurityKey
          ? 'You can add another USB/NFC key. The same key already on this account will be rejected.'
          : 'Insert or tap a USB/NFC security key when prompted. Works across browsers on this PC.',
      },
    };
  };

  const refreshPasskeysList = async (): Promise<PasskeyListItem[]> => {
    const data = await apiService.get<{
      count: number;
      passkeys: PasskeyListItem[];
    }>('/api/passkey/list');
    const list = data.passkeys || [];
    setPasskeysList(list);
    if (typeof data.count === 'number') setPasskeyCount(data.count);
    return list;
  };

  const openPasskeysListDialog = async () => {
    setPasskeysListDialogOpen(true);
    setPasskeysListLoading(true);
    try {
      await refreshPasskeysList();
    } catch (err) {
      console.error('Failed to fetch passkey list:', err);
      setPasskeyError('Could not load registered passkeys.');
      setPasskeysListDialogOpen(false);
    } finally {
      setPasskeysListLoading(false);
    }
  };

  const openRegisterPasskeyDialog = async () => {
    setRegisterPasskeyDialogOpen(true);
    setPasskeysListLoading(true);
    try {
      await refreshPasskeysList();
      if (window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable) {
        const available = await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        setPlatformAuthenticatorAvailable(available);
      } else {
        setPlatformAuthenticatorAvailable(false);
      }
    } catch (err) {
      console.error('Failed to fetch passkeys before register:', err);
      setPlatformAuthenticatorAvailable(null);
    } finally {
      setPasskeysListLoading(false);
    }
  };

  const passkeyAddOptions = useMemo(
    () =>
      analyzePasskeyAddOptions(passkeysList, {
        thisPcHelloUnavailable,
        platformAuthenticatorAvailable,
      }),
    [passkeysList, thisPcHelloUnavailable, platformAuthenticatorAvailable]
  );

  /** Quiet fallback when preference is empty: device-bound/phone → passkey; else totp if enrolled. */
  const computePreferredFallback = (
    hasPasskeys: boolean | number,
    hasTotp: boolean,
    passkeys: PasskeyListItem[]
  ): 'passkey' | 'totp' => {
    const pkCount = typeof hasPasskeys === 'number' ? hasPasskeys : hasPasskeys ? 1 : 0;
    if (pkCount < 1 && hasTotp) return 'totp';
    if (pkCount > 0 && !hasTotp) return 'passkey';
    if (pkCount < 1) return 'totp';
    const hasPasskeyPreferredPath = passkeys.some((p) => {
      if (isPhoneEnrolledPasskey(p)) return true;
      const t = (p.type || '').toLowerCase();
      const l = (p.label || '').toLowerCase();
      return (
        t.includes('windows hello') ||
        t.includes('this device') ||
        t.includes('security key') ||
        l.includes('windows hello') ||
        l.includes('security key')
      );
    });
    return hasPasskeyPreferredPath ? 'passkey' : 'totp';
  };

  const persistPreferredMfaMethod = async (method: 'passkey' | 'totp', opts?: { quiet?: boolean }) => {
    if (!opts?.quiet) {
      setMfaSettingsLoading(true);
      setMfaSettingsNote(null);
    }
    try {
      const data = await apiService.put<{ preferredMfaMethod?: string | null; message?: string }>(
        '/api/mfa-settings/preferred-method',
        { method }
      );
      const pref = (data.preferredMfaMethod || '').toLowerCase();
      setPreferredMfaMethod(pref === 'passkey' || pref === 'totp' ? pref : method);
      if (!opts?.quiet) {
        setMfaSettingsNote(data.message || 'Default login method updated.');
      }
    } catch (err: any) {
      if (!opts?.quiet) {
        setMfaSettingsNote(
          err?.response?.data?.message || err?.message || 'Could not update default login method.'
        );
      }
    } finally {
      if (!opts?.quiet) setMfaSettingsLoading(false);
    }
  };

  const fetchMfaSettings = async () => {
    try {
      const data = await apiService.get<{
        allowMfaEmailRecovery: boolean;
        requireMfaForMicropayLinks?: boolean;
        preferredMfaMethod?: string | null;
        accountRecoveryCodeCount: number;
        hasPasskeys: boolean;
        hasTotp: boolean;
      }>('/api/mfa-settings');
      setAllowMfaEmailRecovery(!!data.allowMfaEmailRecovery);
      setRequireMfaForMicropayLinks(!!data.requireMfaForMicropayLinks);
      const pref = (data.preferredMfaMethod || '').toLowerCase();
      const normalized = pref === 'passkey' || pref === 'totp' ? pref : '';
      setPreferredMfaMethod(normalized);
      setAccountRecoveryCodeCount(data.accountRecoveryCodeCount ?? 0);

      // Legacy empty preference: show computed choice and persist so DB matches UI.
      if (!normalized) {
        const hasPk = !!data.hasPasskeys || passkeyCount > 0;
        const hasTotpFlag = !!data.hasTotp || twoFactorEnabled;
        if (hasPk || hasTotpFlag) {
          let list = passkeysList;
          if (hasPk && list.length === 0) {
            try {
              list = await refreshPasskeysList();
            } catch {
              /* use empty list for fallback */
            }
          }
          const fallback = computePreferredFallback(
            hasPk ? Math.max(passkeyCount, list.length, 1) : 0,
            hasTotpFlag,
            list
          );
          if ((fallback === 'passkey' && hasPk) || (fallback === 'totp' && hasTotpFlag)) {
            await persistPreferredMfaMethod(fallback, { quiet: true });
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch MFA settings:', err);
    }
  };

  const handleSetPreferredMfaMethod = async (method: 'passkey' | 'totp') => {
    await persistPreferredMfaMethod(method);
  };

  /** Align preferred method after enroll/remove so radios stay valid. */
  const syncPreferredAfterFactorChange = async (opts: {
    hasPasskeys: boolean;
    hasTotp: boolean;
    passkeys?: PasskeyListItem[];
  }) => {
    const list = opts.passkeys ?? passkeysList;
    const pkN = opts.hasPasskeys ? Math.max(list.length, 1) : 0;
    const next = computePreferredFallback(pkN, opts.hasTotp, list);
    const current = preferredMfaMethod;
    if (current === 'passkey' && opts.hasPasskeys) return;
    if (current === 'totp' && opts.hasTotp) return;
    if (!opts.hasPasskeys && !opts.hasTotp) return;
    if ((next === 'passkey' && opts.hasPasskeys) || (next === 'totp' && opts.hasTotp)) {
      await persistPreferredMfaMethod(next, { quiet: true });
    }
  };

  const handleToggleMfaEmailRecovery = async (enabled: boolean) => {
    setMfaSettingsLoading(true);
    setMfaSettingsNote(null);
    try {
      const data = await apiService.put<{ allowMfaEmailRecovery: boolean; message?: string }>(
        '/api/mfa-settings/email-recovery',
        { enabled }
      );
      setAllowMfaEmailRecovery(!!data.allowMfaEmailRecovery);
      setMfaSettingsNote(
        data.message ||
          (enabled
            ? 'Email recovery enabled. It only appears at sign-in from a trusted IP with history.'
            : 'Email recovery disabled.')
      );
    } catch (err: any) {
      setMfaSettingsNote(err?.response?.data?.error || err?.message || 'Could not update email recovery setting.');
    } finally {
      setMfaSettingsLoading(false);
    }
  };

  const handleToggleMicropayMfa = async (enabled: boolean) => {
    setMfaSettingsLoading(true);
    setMfaSettingsNote(null);
    try {
      const data = await apiService.put<{ requireMfaForMicropayLinks: boolean; message?: string }>(
        '/api/mfa-settings/micropay-mfa',
        { enabled }
      );
      setRequireMfaForMicropayLinks(!!data.requireMfaForMicropayLinks);
      setMfaSettingsNote(
        data.message ||
          (enabled
            ? 'Two-factor authentication is required for 2WayMicropay purchase and payout links.'
            : 'Purchase and payout links only need your password.')
      );
    } catch (err: any) {
      setMfaSettingsNote(err?.response?.data?.error || err?.message || 'Could not update purchase-link MFA setting.');
    } finally {
      setMfaSettingsLoading(false);
    }
  };

  const handleGenerateAccountRecoveryCodes = async () => {
    setMfaSettingsLoading(true);
    setMfaSettingsNote(null);
    try {
      const data = await apiService.post<{ recoveryCodes: string[]; message?: string }>(
        '/api/mfa-settings/recovery-codes',
        {}
      );
      if (data.recoveryCodes?.length) {
        setPasskeyRecoveryCodes(data.recoveryCodes);
        setAccountRecoveryCodeCount(data.recoveryCodes.length);
        setMfaSettingsNote(
          data.message ||
            'New recovery codes generated. Copy them now — they will not be shown again.'
        );
      }
    } catch (err: any) {
      setMfaSettingsNote(err?.response?.data?.error || err?.message || 'Could not generate recovery codes.');
    } finally {
      setMfaSettingsLoading(false);
    }
  };

  const handleRegisterPasskey = async (modality: 'platform' | 'hybrid' | 'security-key' | 'any') => {
    if (!webAuthnSupported) {
      setPasskeyError(
        'Passkeys need a WebAuthn-capable browser (for example Chrome on a desktop). In the Qixit Mail app, use Authenticator app (TOTP) instead — set it up with the manual key, not the QR code.'
      );
      return;
    }
    setRegisterPasskeyDialogOpen(false);
    setPasskeyLoading(true);
    setPasskeyError(null);
    setPasskeySuccess(null);
    setPasskeyRecoveryCodes([]);
    abortPasskeyCeremony();

    const isOsCreateFailure = (err: unknown) => {
      const e = err as { name?: string; message?: string } | null;
      return (
        /something went wrong|problem saving|try again|abort/i.test(String(e?.message || '')) ||
        e?.name === 'InvalidStateError' ||
        e?.name === 'UnknownError'
      );
    };

    const runCeremony = async (mod: 'platform' | 'hybrid' | 'security-key' | 'any') => {
      abortPasskeyCeremony();
      return runExclusivePasskeyCeremony(async () => {
        const optionsRes = await apiService.post<any>('/api/passkey/register-options', { modality: mod });
        const attestation = await startRegistration({ optionsJSON: optionsRes });
        return apiService.post<{
          success: boolean;
          message?: string;
          warning?: string;
          provider?: string;
          isSyncedPasswordManager?: boolean;
          recoveryCodes?: string[];
        }>('/api/passkey/register', { attestationResponse: attestation });
      });
    };

    try {
      let result: Awaited<ReturnType<typeof runCeremony>>;

      if (modality === 'platform') {
        // Explicit Windows Hello only — do not auto-fallback to password manager (that mixed
        // providers under one "This computer" button and caused conflicting OS options).
        if (thisPcHelloUnavailable) {
          throw new Error(
            'Windows Hello is unavailable here. Use Password manager (Google or Microsoft), Phone, or a security key.'
          );
        }
        result = await runCeremony('platform');
        if (result.isSyncedPasswordManager) {
          markThisPcHelloUnavailable();
        }
      } else {
        result = await runCeremony(modality);
      }

      if (result.recoveryCodes?.length) {
        setPasskeyRecoveryCodes(result.recoveryCodes);
        setAccountRecoveryCodeCount(result.recoveryCodes.length);
        setPasskeySuccess(
          (result.message || 'Passkey registered.') +
            ' Save the recovery codes below — each works once if you lose your devices.'
        );
      } else if (modality === 'platform' && result.isSyncedPasswordManager) {
        setPasskeySuccess(
          result.message ||
            result.warning ||
            `Windows saved this as ${result.provider || 'a password manager'} instead of device-bound Hello. Prefer the Password manager button next time, and keep an authenticator app enabled.`
        );
      } else if (modality === 'any' && result.isSyncedPasswordManager) {
        setPasskeySuccess(
          result.message ||
            result.warning ||
            `Passkey saved as ${result.provider || 'a password manager'}. That may only work in browsers signed into that manager — keep an authenticator app and consider Phone or a security key for other devices.`
        );
      } else {
        setPasskeySuccess(
          result.message ||
            result.warning ||
            'Passkey registered. Prefer a second method (phone, security key, or authenticator app) so another browser or device can still sign in.'
        );
      }
      const list = await refreshPasskeysList();
      await fetchPasskeyStatus();
      await syncPreferredAfterFactorChange({
        hasPasskeys: list.length > 0,
        hasTotp: twoFactorEnabled,
        passkeys: list,
      });
      await fetchMfaSettings();
      } catch (err: any) {
      if (err?.name === 'NotAllowedError') {
        setPasskeyError('Passkey registration was cancelled.');
      } else if (isOsCreateFailure(err)) {
        if (modality === 'hybrid') {
          setPasskeyError(
            err?.name === 'InvalidStateError'
              ? 'This phone already has a passkey for this site (often a leftover from an earlier Qixit/localhost enroll). Remove it in the phone’s password manager / passkeys settings, then try Phone again — or use a security key.'
              : 'Phone/tablet registration failed. Use the QR code (or “Use a phone or tablet”) — do not use Windows Hello fingerprint/PIN on this PC for that option. If this phone already has a passkey for Qixit, remove it on the phone first, or use a security key instead.'
          );
        } else if (modality === 'security-key') {
          setPasskeyError(
            'Security key registration failed. Insert/tap the key when Windows prompts you. Do not choose Windows Hello or Password Manager for this option.'
          );
        } else if (modality === 'platform') {
          markThisPcHelloUnavailable();
          setPasskeyError(
            'Windows Hello could not finish. Use Password manager (Google or Microsoft), Phone, or a security key instead. Keep an authenticator app enabled.'
          );
        } else if (modality === 'any') {
          setPasskeyError(
            'Password manager could not save that passkey. Try again and pick Google or Microsoft Password Manager (not Windows Hello), or use Phone / Security key.'
          );
        } else {
          setPasskeyError(
            'Windows could not save that passkey. Try Password manager again, or use Phone / Security key.'
          );
        }
      } else {
        setPasskeyError(err?.response?.data?.error || err?.message || 'Passkey registration failed.');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const finishRemoveAllPasskeysSuccess = () => {
    setRemovePasskeysDialogOpen(false);
    setRemovePasskeysCode('');
    setPasskeySuccess(
      'All passkeys removed from your Qixit account. Copies may still appear in Windows, Google, or Microsoft Password Manager until you delete them there (Settings → Passkeys / Password Manager). Those leftovers cannot sign you into Qixit after Remove.'
    );
    setPasskeyCount(0);
    setPasskeysList([]);
    setThisPcHelloUnavailable(false);
    try {
      localStorage.removeItem('passkey_this_pc_hello_unavailable');
    } catch {
      /* ignore */
    }
    void syncPreferredAfterFactorChange({
      hasPasskeys: false,
      hasTotp: twoFactorEnabled,
      passkeys: [],
    });
  };

  const handleRemoveAllPasskeysWithCode = async () => {
    setPasskeyLoading(true);
    setPasskeyError(null);
    setPasskeySuccess(null);
    try {
      await apiService.post('/api/passkey/remove-all', { code: removePasskeysCode });
      finishRemoveAllPasskeysSuccess();
    } catch (err: any) {
      setPasskeyError(err?.response?.data?.error || 'Failed to remove passkeys.');
    } finally {
      setPasskeyLoading(false);
    }
  };

  const passkeyConfirmCapabilities = useMemo(() => {
    const isClearComputerKey = (pk: { type?: string; label?: string; transports?: string }) => {
      if (isAmbiguousPasskeyLabel(pk) || isPhoneEnrolledPasskey(pk)) return false;
      const text = `${pk.type || ''} ${pk.label || ''}`.toLowerCase();
      return (
        text.includes('windows hello') ||
        text.includes('microsoft password manager') ||
        text.includes('synced passkey') ||
        text.includes('this computer') ||
        (text.includes('this device') && !text.includes('or phone')) ||
        text.includes('security key')
      );
    };

    const classify = (pk: { type?: string; label?: string; transports?: string }) => {
      const type = (pk.type || '').toLowerCase();
      const label = (pk.label || '').toLowerCase();
      const transports = (pk.transports || '').toLowerCase();
      const text = `${type} ${label}`;
      const ambiguous = isAmbiguousPasskeyLabel(pk);
      const phone = isPhoneEnrolledPasskey(pk) || ambiguous;
      const phoneReason = (() => {
        if (ambiguous) return 'ambiguous-gpm';
        if (type.includes('phone / hybrid') || type === 'phone') return 'type-phone';
        if (label.startsWith('phone (')) return 'label-phone-paren';
        if (/\bphone\b/.test(label) && !label.includes('windows hello')) return 'label-word-phone';
        return phone ? 'unknown' : 'none';
      })();
      const securityKey =
        type.includes('security key') ||
        (transports.split(',').some((t) => ['usb', 'nfc', 'ble'].includes(t.trim())) &&
          !text.includes('password manager') &&
          !text.includes('windows hello'));
      const clearComputer = isClearComputerKey(pk);
      // Synced GPM created via This computer can often assert from Chrome (PC) and via hybrid.
      const computer = clearComputer || (!!label.includes('synced passkey') && transports.includes('hybrid'));
      return {
        phone,
        phoneReason,
        computer,
        securityKey,
        clearComputer,
        ambiguous,
      };
    };

    if (passkeysList.length === 0) {
      return { showPc: false, showPhone: false };
    }

    const classified = passkeysList.map((pk) => ({
      type: pk.type,
      label: pk.label,
      transports: pk.transports,
      ...classify(pk),
    }));
    const hasClearComputer = classified.some((c) => c.clearComputer || c.securityKey);
    // Legacy GPM with no stored enrollment modality is ambiguous — do not offer PC unless a
    // clear computer-bound key (Hello / Microsoft / synced-labeled) is still enrolled.
    let showPc = hasClearComputer;
    let showPhone = classified.some((c) => c.phone || c.ambiguous);
    // Synced (non-ambiguous) GPM with hybrid can use either path.
    if (classified.some((c) => !c.ambiguous && !c.phone && (c.label || '').toLowerCase().includes('synced passkey'))) {
      showPc = true;
      if (classified.some((c) => (c.transports || '').toLowerCase().includes('hybrid'))) {
        showPhone = true;
      }
    }
    // Phone-labeled Google/Microsoft PM still has internal transport — Chrome on this PC can
    // often assert the synced copy. Pure hybrid-only QR frequently fails ("No passkeys available").
    if (
      classified.some((c) => {
        const text = `${c.type || ''} ${c.label || ''}`.toLowerCase();
        const transports = (c.transports || '').toLowerCase();
        return (
          (c.phone || c.ambiguous) &&
          transports.includes('internal') &&
          (text.includes('google password manager') || text.includes('microsoft password manager'))
        );
      })
    ) {
      showPc = true;
    }
    if (!showPc && !showPhone) {
      showPc = true;
      showPhone = true;
    }
    const onlySyncedPasswordManagers = passkeysList.length > 0 && passkeysList.every((pk) => {
      const text = `${pk.type || ''} ${pk.label || ''}`.toLowerCase();
      return text.includes('google password manager') || text.includes('microsoft password manager');
    });
    return { showPc, showPhone, onlySyncedPasswordManagers };
  }, [passkeysList]);

  const handleRemoveAllPasskeysWithPasskey = async (
    modality: 'platform' | 'hybrid' | 'any' = 'any'
  ) => {
    if (!user?.userId) {
      setPasskeyError('Could not confirm with passkey (missing user id).');
      return;
    }
    setPasskeyLoading(true);
    setPasskeyError(null);
    setPasskeySuccess(null);
    abortPasskeyCeremony();
    try {
      const assertion = await runExclusivePasskeyCeremony(async () => {
        const options = await authService.getPasskeyAuthOptions(
          user.userId,
          undefined,
          modality === 'platform',
          undefined,
          modality === 'hybrid'
        );
        return startAuthentication({ optionsJSON: options });
      });
      await apiService.post('/api/passkey/remove-all', { assertionResponse: assertion });
      finishRemoveAllPasskeysSuccess();
    } catch (err: any) {
      if (err?.name === 'NotAllowedError') {
        setPasskeyError('Passkey confirmation was cancelled.');
      } else {
        setPasskeyError(err?.response?.data?.error || err?.message || 'Failed to remove passkeys.');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleRemoveOnePasskey = async (
    passkeyId: string,
    modality: 'platform' | 'hybrid' | 'any' = 'any'
  ) => {
    if (!user?.userId) {
      setPasskeyError('Could not confirm with passkey (missing user id).');
      return;
    }
    // Synced Google/Microsoft PM: platform/hybrid hints make Windows hide the password manager.
    const effectiveModality =
      passkeyConfirmCapabilities.onlySyncedPasswordManagers ? 'any' : modality;
    setPasskeyLoading(true);
    setPasskeyError(null);
    setPasskeySuccess(null);
    abortPasskeyCeremony();
    try {
      const assertion = await runExclusivePasskeyCeremony(async () => {
        const options = await authService.getPasskeyAuthOptions(
          user.userId,
          undefined,
          effectiveModality === 'platform',
          undefined,
          effectiveModality === 'hybrid'
        );
        return startAuthentication({ optionsJSON: options });
      });
      await apiService.post(`/api/passkey/remove/${passkeyId}`, { assertionResponse: assertion });
      const remaining = await refreshPasskeysList();
      setPasskeySuccess('Passkey removed.');
      setRemoveOneCode('');
      setRemoveOneTargetId(null);
      // Recompute Add-passkey flags from remaining keys (phone GPM must not keep This computer blocked).
      const stillHasComputerBound = remaining.some(
        (p) => !isPhoneEnrolledPasskey(p) && !isAmbiguousPasskeyLabel(p)
      );
      if (!stillHasComputerBound) {
        setThisPcHelloUnavailable(false);
        try {
          localStorage.removeItem('passkey_this_pc_hello_unavailable');
        } catch {
          /* ignore */
        }
      }
      await syncPreferredAfterFactorChange({
        hasPasskeys: remaining.length > 0,
        hasTotp: twoFactorEnabled,
        passkeys: remaining,
      });
    } catch (err: any) {
      if (err?.name === 'NotAllowedError') {
        setPasskeyError('Passkey confirmation was cancelled.');
      } else {
        setPasskeyError(err?.response?.data?.error || err?.message || 'Failed to remove passkey.');
      }
    } finally {
      setPasskeyLoading(false);
    }
  };

  const handleRemoveOnePasskeyWithCode = async (passkeyId: string) => {
    setPasskeyLoading(true);
    setPasskeyError(null);
    setPasskeySuccess(null);
    try {
      await apiService.post(`/api/passkey/remove/${passkeyId}`, { code: removeOneCode });
      const remaining = await refreshPasskeysList();
      setPasskeySuccess('Passkey removed.');
      setRemoveOneCode('');
      setRemoveOneTargetId(null);
      const stillHasComputerBound = remaining.some(
        (p) => !isPhoneEnrolledPasskey(p) && !isAmbiguousPasskeyLabel(p)
      );
      if (!stillHasComputerBound) {
        setThisPcHelloUnavailable(false);
        try {
          localStorage.removeItem('passkey_this_pc_hello_unavailable');
        } catch {
          /* ignore */
        }
      }
      await syncPreferredAfterFactorChange({
        hasPasskeys: remaining.length > 0,
        hasTotp: twoFactorEnabled,
        passkeys: remaining,
      });
    } catch (err: any) {
      setPasskeyError(err?.response?.data?.error || err?.message || 'Failed to remove passkey.');
    } finally {
      setPasskeyLoading(false);
    }
  };

  const applyBusinessAccountEdits = (business: BusinessAccountInfo | null | undefined) => {
    const snapshot = {
      businessName: business?.businessName || '',
      dbaName: business?.dbaName || '',
      fein: business?.fein || '',
      websiteUrl: business?.websiteUrl || '',
      contactName: business?.contactName || '',
      phoneNumber: business?.phoneNumber || '',
      email: business?.email || '',
      address1: business?.address1 || '',
      address2: business?.address2 || '',
      city: business?.city || '',
      state: business?.state || '',
      zipCode: business?.zipCode || '',
      country: business?.country || '',
    };
    setBusinessProfileEdits(snapshot);
    setSavedBusinessProfile({
      phoneNumber: snapshot.phoneNumber,
      address1: snapshot.address1,
      address2: snapshot.address2,
      city: snapshot.city,
      state: snapshot.state,
      zipCode: snapshot.zipCode,
    });
  };

  const fetchAccountDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const details = await userAccountService.getAccountDetails();
      setCompositeDetails(details);
      const personal = details.personalAccount;

      setSavedPhoneNumber(personal.phoneNumber || '');
      setFormData({
        firstName: personal.firstName || '',
        lastName: personal.lastName || '',
        phoneNumber: personal.phoneNumber || '',
        email: personal.email || '',
        address1: personal.address1 || '',
        address2: personal.address2 || '',
        city: personal.city || '',
        state: personal.state || '',
        zipCode: personal.zipCode || '',
        country: personal.country || '',
      });
      applyBusinessAccountEdits(details.businessAccount);

      const countryValue = personal.country || '';
      let foundCountry = getCountryByCode(countryValue) || getCountryByName(countryValue);
      if (!foundCountry && countryValue.toUpperCase() === 'USA') {
        foundCountry = getCountryByCode('US');
      }
      setSelectedCountry(foundCountry || null);

      if (foundCountry && personal.state) {
        const subs = getSubdivisions(foundCountry.code);
        const foundSub = subs.find(s =>
          s.code.toUpperCase() === personal.state?.toUpperCase() ||
          s.name.toLowerCase() === personal.state?.toLowerCase()
        );
        setSelectedSubdivision(foundSub || null);
      }

      if (personal.dateOfBirth) {
        setDateOfBirth(dayjs(personal.dateOfBirth).format('YYYY-MM-DD'));
      } else {
        setDateOfBirth('');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load account details');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof UpdateUserAccountRequest) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleOpenEmailDialog = () => {
    setNewEmail('');
    setVerificationCode('');
    setEmailChangeStep('enter');
    setEmailChangeError(null);
    setEmailChangeSuccess(null);
    setEmailDialogOpen(true);
  };

  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
    setNewEmail('');
    setVerificationCode('');
    setEmailChangeStep('enter');
    setEmailChangeError(null);
    setEmailChangeSuccess(null);
  };

  const handleRequestEmailChange = async () => {
    try {
      setEmailChangeLoading(true);
      setEmailChangeError(null);
      
      const result = await userAccountService.requestEmailChange(newEmail);
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to request email change');
      }
      
      setEmailChangeSuccess(result.message);
      setEmailChangeStep('verify');
    } catch (err: any) {
      // Handle axios error structure
      const message = err.response?.data?.message || err.message || 'Failed to request email change';
      setEmailChangeError(message);
    } finally {
      setEmailChangeLoading(false);
    }
  };

  const handleConfirmEmailChange = async () => {
    try {
      setEmailChangeLoading(true);
      setEmailChangeError(null);
      
      const result = await userAccountService.confirmEmailChange(newEmail, verificationCode);
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to confirm email change');
      }
      
      setEmailChangeSuccess('Email address updated successfully!');
      
      // Refresh account details after a short delay
      setTimeout(async () => {
        handleCloseEmailDialog();
        await fetchAccountDetails();
        setSuccess('Email address has been updated.');
      }, 1500);
    } catch (err: any) {
      // Handle axios error structure
      const message = err.response?.data?.message || err.message || 'Failed to confirm email change';
      setEmailChangeError(message);
    } finally {
      setEmailChangeLoading(false);
    }
  };

  const handleOpenBusinessEmailDialog = () => {
    setBusinessNewEmail('');
    setBusinessEmailVerificationCode('');
    setBusinessEmailChangeStep('enter');
    setBusinessEmailChangeError(null);
    setBusinessEmailChangeSuccess(null);
    setBusinessEmailDialogOpen(true);
  };

  const handleCloseBusinessEmailDialog = () => {
    setBusinessEmailDialogOpen(false);
    setBusinessNewEmail('');
    setBusinessEmailVerificationCode('');
    setBusinessEmailChangeStep('enter');
    setBusinessEmailChangeError(null);
    setBusinessEmailChangeSuccess(null);
  };

  const handleRequestBusinessEmailChange = async () => {
    const credentialId = businessAccount?.credentialId;
    if (!credentialId) return;

    try {
      setBusinessEmailChangeLoading(true);
      setBusinessEmailChangeError(null);

      const result = await userAccountService.requestBusinessEmailChange(credentialId, businessNewEmail);

      if (!result.success) {
        throw new Error(result.message || 'Failed to request business email change');
      }

      setBusinessEmailChangeSuccess(result.message);
      setBusinessEmailChangeStep('verify');
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to request business email change';
      setBusinessEmailChangeError(message);
    } finally {
      setBusinessEmailChangeLoading(false);
    }
  };

  const handleConfirmBusinessEmailChange = async () => {
    const credentialId = businessAccount?.credentialId;
    if (!credentialId) return;

    try {
      setBusinessEmailChangeLoading(true);
      setBusinessEmailChangeError(null);

      const result = await userAccountService.confirmBusinessEmailChange(
        credentialId,
        businessNewEmail,
        businessEmailVerificationCode,
      );

      if (!result.success) {
        throw new Error(result.message || 'Failed to confirm business email change');
      }

      setBusinessEmailChangeSuccess('Business email address updated successfully!');

      setTimeout(async () => {
        handleCloseBusinessEmailDialog();
        await fetchAccountDetails();
        setSuccess('Business email address has been updated.');
      }, 1500);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to confirm business email change';
      setBusinessEmailChangeError(message);
    } finally {
      setBusinessEmailChangeLoading(false);
    }
  };

  // 2FA Handlers
  const handleSetup2FA = async () => {
    try {
      setTwoFactorLoading(true);
      setTwoFactorError(null);
      
      const response = await userAccountService.setupTwoFactor();
      
      if (response.success) {
        setQrCodeData(response.qrCodeDataUrl);
        setManualKey(response.manualEntryKey);
        setBackupCodes(response.backupCodes);
        setTwoFactorDialogOpen(true);
      } else {
        throw new Error(response.message || 'Failed to initialize 2FA setup');
      }
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to initialize 2FA setup';
      setTwoFactorError(message);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    try {
      setTwoFactorLoading(true);
      setTwoFactorError(null);
      
      const response = await userAccountService.verifyTwoFactorSetup(totpVerificationCode);
      
      if (response.success) {
        setTwoFactorEnabled(true);
        setTwoFactorPendingSetup(false);
        setTwoFactorDialogOpen(false);
        setTotpVerificationCode('');
        setTwoFactorSuccess('Two-factor authentication enabled successfully!');
        setSuccess('Two-factor authentication has been enabled for your account.');
        await syncPreferredAfterFactorChange({
          hasPasskeys: passkeyCount > 0,
          hasTotp: true,
          passkeys: passkeysList,
        });
      } else {
        throw new Error(response.message || 'Invalid verification code');
      }
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to verify 2FA code';
      setTwoFactorError(message);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    try {
      setTwoFactorLoading(true);
      setTwoFactorError(null);

      const response = await userAccountService.disableTwoFactor({ code: disableCode });

      if (response.success) {
        setTwoFactorEnabled(false);
        setDisableDialogOpen(false);
        setDisableCode('');
        setSuccess('Two-factor authentication has been disabled.');
        await syncPreferredAfterFactorChange({
          hasPasskeys: passkeyCount > 0,
          hasTotp: false,
          passkeys: passkeysList,
        });
      } else {
        throw new Error(response.message || 'Failed to disable 2FA');
      }
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Invalid verification code';
      setTwoFactorError(message);
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleDisable2FAWithPasskey = async () => {
    if (!user?.userId) {
      setTwoFactorError('Could not confirm with passkey (missing user id).');
      return;
    }
    try {
      setTwoFactorLoading(true);
      setTwoFactorError(null);

      const assertion = await runExclusivePasskeyCeremony(async () => {
        const options = await authService.getPasskeyAuthOptions(user.userId);
        return startAuthentication({ optionsJSON: options });
      });

      const response = await userAccountService.disableTwoFactor({ assertionResponse: assertion });

      if (response.success) {
        setTwoFactorEnabled(false);
        setDisableDialogOpen(false);
        setDisableCode('');
        setSuccess('Authenticator app disabled after passkey confirmation.');
        await syncPreferredAfterFactorChange({
          hasPasskeys: passkeyCount > 0,
          hasTotp: false,
          passkeys: passkeysList,
        });
      } else {
        throw new Error(response.message || 'Failed to disable 2FA');
      }
    } catch (err: any) {
      if (err?.name === 'NotAllowedError') {
        setTwoFactorError('Passkey confirmation was cancelled.');
      } else {
        const message =
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'Passkey confirmation failed.';
        setTwoFactorError(message);
      }
    } finally {
      setTwoFactorLoading(false);
    }
  };

  const handleClose2FADialog = () => {
    setTwoFactorDialogOpen(false);
    setQrCodeData(null);
    setManualKey(null);
    setBackupCodes([]);
    setTotpVerificationCode('');
    setTwoFactorError(null);
  };

  const phoneChangedUnsaved = (formData.phoneNumber || '').trim() !== savedPhoneNumber.trim();
  const phoneNeedsVerification = phoneChangedUnsaved || !(personalAccount?.isPhoneVerified ?? false);

  const personalDetailsIncomplete =
    !(formData.firstName || '').trim() ||
    !(formData.lastName || '').trim() ||
    !(formData.phoneNumber || '').trim();
  const showProfileCompletionBanner = personalDetailsIncomplete && !profileCompletionDismissed;

  const dismissProfileCompletionBanner = () => {
    setProfileCompletionDismissed(true);
    try {
      localStorage.setItem('profile_completion_nudge_dismissed', '1');
    } catch {
      // Non-fatal: banner simply reappears next session.
    }
  };

  const businessPhoneChangedUnsaved =
    (businessProfileEdits.phoneNumber || '').trim() !== (savedBusinessProfile.phoneNumber || '').trim();
  const businessPhoneNeedsVerification =
    businessPhoneChangedUnsaved || !(businessAccount?.isPhoneVerified ?? false);

  const businessAddressChangedUnsaved =
    (businessProfileEdits.address1 || '').trim() !== (savedBusinessProfile.address1 || '').trim() ||
    (businessProfileEdits.address2 || '').trim() !== (savedBusinessProfile.address2 || '').trim() ||
    (businessProfileEdits.city || '').trim() !== (savedBusinessProfile.city || '').trim() ||
    (businessProfileEdits.state || '').trim() !== (savedBusinessProfile.state || '').trim() ||
    (businessProfileEdits.zipCode || '').trim() !== (savedBusinessProfile.zipCode || '').trim();
  const businessAddressNeedsVerification =
    businessAddressChangedUnsaved || !(businessAccount?.isAddressVerified ?? false);

  const businessEmailNeedsVerification = !(businessAccount?.isEmailVerified ?? false);

  const businessTextFieldLabelProps = { shrink: true as const };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <NativeDisplaySettings />
      <NativeBiometricSettings />

      {onManageAliases && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Link component="button" variant="body2" onClick={onManageAliases} sx={{ fontWeight: 500 }}>
            Create and Manage Aliases
          </Link>
        </Box>
      )}

      {/* Two-Factor Authentication — above main settings card */}
      {!isMinor && (
      <Card elevation={3} sx={{ mb: 3 }} id="two-factor-auth">
        <CardContent>
          <CollapsibleSettingsSection
            sectionId="two-factor-auth"
            expanded={isExpanded('two-factor-auth')}
            onToggle={() => toggleSection('two-factor-auth')}
            title={(
              <>
                <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SecurityIcon color="primary" />
                  Two-Factor Authentication
                  {isBusinessAccount ? ' (this business account)' : ''}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isBusinessAccount
                    ? 'Security for this business Mailbox only — separate from your personal account MFA.'
                    : 'Add an extra layer of security for this personal Mailbox — require a passkey or authenticator code at login.'}
                </Typography>
              </>
            )}
          >
          {twoFactorError && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }} onClose={() => setTwoFactorError(null)}>
              {twoFactorError}
            </Alert>
          )}

          {twoFactorSuccess && (
            <Alert severity="success" sx={{ mt: 2, mb: 2 }} onClose={() => setTwoFactorSuccess(null)}>
              {twoFactorSuccess}
            </Alert>
          )}

          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                    Authenticator App (TOTP)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {twoFactorPendingSetup
                      ? 'Authenticator entry started — enter a 6-digit code to finish enabling TOTP.'
                      : nativeShell
                        ? 'On this phone, use Enter setup key in your authenticator app (not QR scan). Works on every device.'
                        : 'Use an app like Google Authenticator or Authy to generate verification codes'}
                  </Typography>
                </Box>
                {twoFactorEnabled ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip label="Enabled" color="success" icon={<VerifiedIcon />} />
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => setDisableDialogOpen(true)}
                    >
                      Disable
                    </Button>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {twoFactorPendingSetup && (
                      <Chip label="Setup incomplete" color="warning" size="small" />
                    )}
                    <Button
                      variant="contained"
                      startIcon={twoFactorLoading ? <CircularProgress size={20} /> : <QrCodeIcon />}
                      onClick={handleSetup2FA}
                      disabled={twoFactorLoading}
                    >
                      {twoFactorPendingSetup ? 'Finish setup' : 'Set Up'}
                    </Button>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Passkey row */}
          {passkeyError && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setPasskeyError(null)}>
              {passkeyError}
            </Alert>
          )}
          {passkeySuccess && (
            <Alert severity="success" sx={{ mt: 2 }} onClose={() => setPasskeySuccess(null)}>
              {passkeySuccess}
            </Alert>
          )}
          {!webAuthnSupported && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Passkey registration is not available inside the Qixit Mail app shell (Android WebView does not
              expose WebAuthn). Use <strong>Authenticator app</strong> below for mobile login, or add a passkey
              from <strong>mail.qixit.com in Chrome</strong> on a desktop.
            </Alert>
          )}
          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                    Passkey (this computer, phone, or security key)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Sign in with biometrics, PIN, phone, or a hardware key. Password-manager passkeys can be
                    browser-specific. Also add a phone, tablet, or security-key passkey, or enable an
                    authenticator app, so you’re not locked out on another browser or device.
                    {passkeyCount > 0 && (
                      <>
                        {' '}
                        <Box
                          component="button"
                          type="button"
                          onClick={() => void openPasskeysListDialog()}
                          sx={{
                            border: 0,
                            background: 'none',
                            p: 0,
                            m: 0,
                            color: 'primary.main',
                            textDecoration: 'underline',
                            cursor: 'pointer',
                            font: 'inherit',
                          }}
                        >
                          {passkeyCount} passkey{passkeyCount > 1 ? 's' : ''} registered
                        </Box>
                        .
                      </>
                    )}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {passkeyCount > 0 && (
                    <>
                      <Tooltip title="Click to see which passkeys are registered">
                        <Chip
                          label={`${passkeyCount} registered`}
                          color="success"
                          icon={<FingerprintIcon />}
                          size="small"
                          onClick={() => void openPasskeysListDialog()}
                          sx={{ cursor: 'pointer' }}
                        />
                      </Tooltip>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={() => setRemovePasskeysDialogOpen(true)}
                        disabled={passkeyLoading}
                      >
                        Remove
                      </Button>
                    </>
                  )}
                  <Button
                    variant={passkeyCount > 0 ? 'outlined' : 'contained'}
                    size="small"
                    startIcon={passkeyLoading ? <CircularProgress size={18} /> : <FingerprintIcon />}
                    onClick={() => void openRegisterPasskeyDialog()}
                    disabled={passkeyLoading || !webAuthnSupported}
                  >
                    {passkeyCount > 0 ? 'Add Another' : 'Register Passkey'}
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* MFA recovery: codes + discouraged email opt-in */}
          {mfaSettingsNote && (
            <Alert
              severity="info"
              sx={{ mt: 2 }}
              onClose={() => setMfaSettingsNote(null)}
            >
              {mfaSettingsNote}
            </Alert>
          )}
          {passkeyRecoveryCodes.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Account recovery codes (save now — shown once)
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: 13,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {passkeyRecoveryCodes.join('\n')}
              </Box>
              <Button
                size="small"
                sx={{ mt: 1 }}
                onClick={() => {
                  void navigator.clipboard.writeText(passkeyRecoveryCodes.join('\n'));
                  setMfaSettingsNote('Recovery codes copied to clipboard.');
                }}
              >
                Copy codes
              </Button>
            </Alert>
          )}
          <Card variant="outlined" sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
                MFA recovery options
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Prefer a phone or security-key passkey plus an authenticator app so another browser is not
                locked out. One-time recovery codes are the last resort. Email recovery is weaker and stays
                off unless you opt in.
              </Typography>
              <Stack spacing={2}>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                    Default login method
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Sign-in starts with this method when enrolled. You can switch during MFA (Try another way).
                  </Typography>
                  <RadioGroup
                    value={
                      preferredMfaMethod === 'passkey' || preferredMfaMethod === 'totp'
                        ? preferredMfaMethod
                        : computePreferredFallback(passkeyCount, twoFactorEnabled, passkeysList)
                    }
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v === 'passkey' || v === 'totp') {
                        void handleSetPreferredMfaMethod(v);
                      }
                    }}
                  >
                    <FormControlLabel
                      value="passkey"
                      control={<Radio size="small" disabled={mfaSettingsLoading || passkeyCount < 1} />}
                      label="Passkey first"
                    />
                    <FormControlLabel
                      value="totp"
                      control={<Radio size="small" disabled={mfaSettingsLoading || !twoFactorEnabled} />}
                      label="Authenticator app first"
                    />
                  </RadioGroup>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Account recovery codes
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {accountRecoveryCodeCount > 0
                        ? `${accountRecoveryCodeCount} unused code${accountRecoveryCodeCount === 1 ? '' : 's'} on file (values hidden).`
                        : 'None on file. Generate after registering a passkey or authenticator.'}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={mfaSettingsLoading}
                    onClick={handleGenerateAccountRecoveryCodes}
                  >
                    {accountRecoveryCodeCount > 0 ? 'Regenerate codes' : 'Generate codes'}
                  </Button>
                </Box>
                <Divider />
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={allowMfaEmailRecovery}
                        disabled={mfaSettingsLoading}
                        onChange={(_, checked) => void handleToggleMfaEmailRecovery(checked)}
                        color="warning"
                      />
                    }
                    label="Allow email MFA recovery (not recommended)"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 6 }}>
                    Even when enabled, email codes only appear at sign-in from a trusted network you have
                    used successfully several times (IPv4 exact or IPv6 /64). Prefer recovery codes instead.
                  </Typography>
                </Box>
                <Divider />
                <Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={requireMfaForMicropayLinks}
                        disabled={mfaSettingsLoading}
                        onChange={(_, checked) => void handleToggleMicropayMfa(checked)}
                      />
                    }
                    label="Require two-factor authentication for 2WayMicropay purchase links"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ pl: 6 }}>
                    Off by default. PurchaseFast and PaymentFast then only need your password. MailBox and
                    other portals still require two-factor when enrolled.
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
          </CollapsibleSettingsSection>
        </CardContent>
      </Card>
      )}

      {onChangePasswordClick && (
        <Box sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<LockIcon />}
            onClick={onChangePasswordClick}
          >
            Change password
          </Button>
        </Box>
      )}

      <Card elevation={3}>
        <CardContent>
          {isMinor && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Verification and security settings are managed by your parent account. You can update basic profile details below.
            </Alert>
          )}

          {aliasContext && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Signed in as business member <strong>{aliasContext.aliasUsername}</strong>
              {aliasContext.jointAccountName ? ` on ${aliasContext.jointAccountName}` : ''}.
              Personal information below applies to you only.
            </Alert>
          )}

          {showBusinessSection && !canEditBusinessInfo && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Business information is view-only for your role. Contact an account owner to make changes.
            </Alert>
          )}

          {error && !idVerificationInlineError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
              {success}
            </Alert>
          )}

          {showBusinessSection && businessAccount && (
            <CollapsibleSettingsSection
              sectionId="business-information"
              expanded={isExpanded('business-information')}
              onToggle={() => toggleSection('business-information')}
              sx={{
                p: 2,
                borderRadius: 1,
                border: 1,
                borderColor: 'success.light',
                bgcolor: (theme) => (
                  theme.palette.mode === 'dark'
                    ? 'rgba(76, 175, 80, 0.12)'
                    : 'rgba(76, 175, 80, 0.08)'
                ),
              }}
              title={(
                <Typography variant="h6" color="success.dark" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BusinessIcon />
                  Business Information
                </Typography>
              )}
            >
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Business Name"
                      fullWidth
                      value={businessProfileEdits.businessName}
                      onChange={(e) => handleBusinessProfileFieldChange('businessName', e.target.value)}
                      disabled={!canEditBusinessInfo}
                      InputLabelProps={businessTextFieldLabelProps}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="DBA (if provided)"
                      fullWidth
                      value={businessProfileEdits.dbaName}
                      disabled
                      InputLabelProps={businessTextFieldLabelProps}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="FEIN"
                      fullWidth
                      value={businessProfileEdits.fein}
                      onChange={(e) => handleBusinessProfileFieldChange('fein', e.target.value)}
                      disabled={!canEditBusinessInfo}
                      InputLabelProps={businessTextFieldLabelProps}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Website"
                      fullWidth
                      value={businessProfileEdits.websiteUrl}
                      onChange={(e) => handleBusinessProfileFieldChange('websiteUrl', e.target.value)}
                      disabled={!canEditBusinessInfo}
                      InputLabelProps={businessTextFieldLabelProps}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Business Contact Name"
                      fullWidth
                      value={businessProfileEdits.contactName}
                      onChange={(e) => handleBusinessProfileFieldChange('contactName', e.target.value)}
                      disabled={!canEditBusinessInfo}
                      InputLabelProps={businessTextFieldLabelProps}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Business Phone"
                      fullWidth
                      value={businessProfileEdits.phoneNumber}
                      onChange={(e) => handleBusinessProfileFieldChange('phoneNumber', e.target.value)}
                      disabled={!canEditBusinessInfo}
                      InputLabelProps={businessTextFieldLabelProps}
                    />
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      {!businessPhoneNeedsVerification ? (
                        <Chip size="small" icon={<VerifiedIcon />} label="Verified" color="success" variant="outlined" />
                      ) : (
                        <>
                          <Chip
                            size="small"
                            icon={<WarningIcon />}
                            label={businessPhoneChangedUnsaved ? 'Pending verification for new number' : 'Not verified'}
                            color="warning"
                            variant="outlined"
                          />
                          {canEditBusinessInfo && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<PhoneIcon />}
                              onClick={handleOpenBusinessPhoneDialog}
                              sx={{ py: 0.25 }}
                            >
                              Verify Phone
                            </Button>
                          )}
                        </>
                      )}
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                      <Box sx={{ flex: 1 }}>
                        <TextField
                          label="Business Email"
                          fullWidth
                          type="email"
                          value={businessProfileEdits.email}
                          disabled
                          InputLabelProps={businessTextFieldLabelProps}
                          InputProps={{
                            startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />,
                          }}
                        />
                        <Box sx={{ mt: 0.5 }}>
                          {!businessEmailNeedsVerification ? (
                            <Chip size="small" icon={<VerifiedIcon />} label="Verified" color="success" variant="outlined" />
                          ) : (
                            <Chip size="small" icon={<WarningIcon />} label="Not verified" color="warning" variant="outlined" />
                          )}
                        </Box>
                      </Box>
                      {canEditBusinessInfo && (
                        <Button
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={handleOpenBusinessEmailDialog}
                          sx={{ mt: 1, whiteSpace: 'nowrap' }}
                        >
                          Change Email
                        </Button>
                      )}
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Business Mailing Address
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {!businessAddressNeedsVerification ? (
                          <Chip size="small" icon={<VerifiedIcon />} label="Verified" color="success" variant="outlined" />
                        ) : (
                          <Chip size="small" icon={<WarningIcon />} label="Not verified" color="warning" variant="outlined" />
                        )}
                        {canEditBusinessInfo && businessAddressNeedsVerification && (
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<HomeIcon />}
                            onClick={handleOpenBusinessAddressDialog}
                          >
                            Verify Address
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Business Address Line 1"
                      fullWidth
                      value={businessProfileEdits.address1}
                      onChange={(e) => handleBusinessProfileFieldChange('address1', e.target.value)}
                      disabled={!canEditBusinessInfo}
                      InputLabelProps={businessTextFieldLabelProps}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Business Address Line 2"
                      fullWidth
                      value={businessProfileEdits.address2}
                      onChange={(e) => handleBusinessProfileFieldChange('address2', e.target.value)}
                      disabled={!canEditBusinessInfo}
                      InputLabelProps={businessTextFieldLabelProps}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="City"
                      fullWidth
                      value={businessProfileEdits.city}
                      onChange={(e) => handleBusinessProfileFieldChange('city', e.target.value)}
                      disabled={!canEditBusinessInfo}
                      InputLabelProps={businessTextFieldLabelProps}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="State"
                      fullWidth
                      value={businessProfileEdits.state}
                      onChange={(e) => handleBusinessProfileFieldChange('state', e.target.value)}
                      disabled={!canEditBusinessInfo}
                      InputLabelProps={businessTextFieldLabelProps}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="ZIP Code"
                      fullWidth
                      value={businessProfileEdits.zipCode}
                      onChange={(e) => handleBusinessProfileFieldChange('zipCode', e.target.value)}
                      disabled={!canEditBusinessInfo}
                      InputLabelProps={businessTextFieldLabelProps}
                    />
                  </Grid>
                </Grid>
                <BusinessRedemptionIdentitySection
                  onVerifyIdentity={handleOpenIdentityVerificationConfirm}
                  isIdVerified={!!verificationStatus?.isIdVerified}
                  idVerificationStarting={idVerificationStarting}
                />
                {canEditBusinessInfo && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={businessSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                      onClick={handleSaveBusinessInfo}
                      disabled={businessSaving || !isBusinessProfileDirty()}
                    >
                      {businessSaving ? 'Saving…' : 'Save Business Info'}
                    </Button>
                  </Box>
                )}
            </CollapsibleSettingsSection>
          )}

          <CollapsibleSettingsSection
            sectionId="personal-information"
            expanded={isExpanded('personal-information')}
            onToggle={() => toggleSection('personal-information')}
            sx={{ mt: showBusinessSection && businessAccount ? 3 : 0 }}
            title={(
              <Typography variant="h6" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon />
                Personal Information
              </Typography>
            )}
          >
            {showProfileCompletionBanner && (
              <Alert
                severity="info"
                sx={{ mb: 2 }}
                onClose={dismissProfileCompletionBanner}
                data-testid="profile-nudge"
                slotProps={{
                  closeButton: { 'data-testid': 'profile-nudge-dismiss' } as any,
                }}
              >
                Add your name and phone number to finish setting up your account. A name lets people
                recognize you on messages and payments, and a verified phone number unlocks account
                recovery, withdrawals, and higher transaction limits.
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="First Name"
                  name="profile-first-name"
                  autoComplete="off"
                  fullWidth
                  value={formData.firstName || ''}
                  onChange={handleChange('firstName')}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Last Name"
                  name="profile-last-name"
                  autoComplete="off"
                  fullWidth
                  value={formData.lastName || ''}
                  onChange={handleChange('lastName')}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Phone Number"
                  name="profile-phone"
                  autoComplete="off"
                  fullWidth
                  value={formData.phoneNumber || ''}
                  onChange={handleChange('phoneNumber')}
                  placeholder="(555) 123-4567"
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  {!phoneNeedsVerification ? (
                    <Chip size="small" icon={<VerifiedIcon />} label="Verified" color="success" variant="outlined" />
                  ) : (
                    <>
                      <Chip
                        size="small"
                        icon={<WarningIcon />}
                        label={phoneChangedUnsaved ? 'Pending verification for new number' : 'Not verified'}
                        color="warning"
                        variant="outlined"
                      />
                      <Button size="small" variant="outlined" startIcon={<PhoneIcon />} onClick={handleOpenPhoneDialog} sx={{ py: 0.25 }}>
                        Verify Phone
                      </Button>
                    </>
                  )}
                </Box>
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      label="Email Address"
                      fullWidth
                      type="email"
                      value={personalAccount?.email || ''}
                      disabled
                      placeholder="your@email.com"
                      InputProps={{
                        startAdornment: <EmailIcon color="action" sx={{ mr: 1 }} />,
                      }}
                    />
                    <Box sx={{ mt: 0.5 }}>
                      {personalAccount?.isEmailVerified ? (
                        <Chip size="small" icon={<VerifiedIcon />} label="Verified" color="success" variant="outlined" />
                      ) : (
                        <Chip size="small" icon={<WarningIcon />} label="Not verified" color="warning" variant="outlined" />
                      )}
                    </Box>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={handleOpenEmailDialog}
                    sx={{ mt: 1, whiteSpace: 'nowrap' }}
                  >
                    Change Email
                  </Button>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Date of Birth"
                  name="profile-birthdate"
                  autoComplete="off"
                  type="date"
                  fullWidth
                  value={dateOfBirth}
                  onChange={(e) => setDateOfBirth(e.target.value)}
                  helperText="Required for identity verification (year 1900 or later)"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: '1900-01-01',
                    max: dayjs().subtract(13, 'year').format('YYYY-MM-DD')
                  }}
                />
              </Grid>

              {!isMinor && (
              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
                  <BadgeIcon color={verificationStatus?.isIdVerified ? 'success' : 'action'} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="medium">Identity Verification</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Verify your identity with a government-issued ID
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Verification and re-verification each charge ${ID_VERIFICATION_FEE.toFixed(2)}.
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <FormControlLabel
                        sx={{ ml: 0 }}
                        control={(
                          <Switch
                            size="small"
                            checked={autoApproveAllRequests}
                            onChange={(event) => handleAutoApproveToggle(event.target.checked)}
                            disabled={autoApproveLoading || autoApproveSaving}
                          />
                        )}
                        label={
                          <Typography variant="caption" color="text.secondary">
                            Automatically approve sharing of your verified name, general location, ID portrait, and live capture with anyone requesting verification. Mailing address is never auto-shared.
                          </Typography>
                        }
                      />
                    </Box>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {verificationStatus?.isIdVerified ? (
                      <Chip size="small" icon={<VerifiedIcon />} label="Verified" color="success" variant="outlined" />
                    ) : (
                      <Chip size="small" label="Not Verified" color="warning" variant="outlined" />
                    )}
                    <Button
                      variant={verificationStatus?.isIdVerified ? 'outlined' : 'contained'}
                      size="small"
                      onClick={handleOpenIdentityVerificationConfirm}
                      disabled={idVerificationStarting}
                      startIcon={
                        idVerificationStarting
                          ? <CircularProgress size={14} color="inherit" />
                          : <BadgeIcon />
                      }
                    >
                      {idVerificationStarting
                        ? 'Starting…'
                        : (verificationStatus?.isIdVerified ? 'Re-verify Identity' : 'Verify Identity')}
                    </Button>
                    {verificationStatus?.isIdVerified && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleOpenIdentityPreview}
                        startIcon={<FingerprintIcon />}
                      >
                        View Verification Preview
                      </Button>
                    )}
                  </Stack>
                </Box>
                {idVerificationInlineError && (
                  <Alert severity="error" sx={{ mt: 1.25 }} onClose={() => setError(null)}>
                    {idVerificationInlineError}
                  </Alert>
                )}
              </Grid>
              )}
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save Personal Info'}
              </Button>
            </Box>
          </CollapsibleSettingsSection>

          <Divider sx={{ my: 3 }} />

          <CollapsibleSettingsSection
            sectionId="mailing-address"
            expanded={isExpanded('mailing-address')}
            onToggle={() => toggleSection('mailing-address')}
            title={(
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                <Typography variant="h6" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HomeIcon />
                  Personal Mailing Address
                  {personalAccount?.isAddressVerified ? (
                    <Chip size="small" icon={<VerifiedIcon />} label="Verified" color="success" variant="outlined" sx={{ ml: 1 }} />
                  ) : (
                    <Chip size="small" icon={<WarningIcon />} label="Not verified" color="warning" variant="outlined" sx={{ ml: 1 }} />
                  )}
                </Typography>
                {!personalAccount?.isAddressVerified && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<HomeIcon />}
                    onClick={(event) => {
                      event.stopPropagation();
                      handleOpenAddressDialog();
                    }}
                  >
                    Verify Address
                  </Button>
                )}
              </Box>
            )}
          >
            <Grid container spacing={2}>
              {/* Address Line 1 - full width */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Address Line 1"
                  name="profile-address-1"
                  autoComplete="off"
                  fullWidth
                  value={formData.address1 || ''}
                  onChange={handleChange('address1')}
                  placeholder="123 Main Street"
                />
              </Grid>
              {/* Address Line 2 - full width */}
              <Grid size={{ xs: 12 }}>
                <TextField
                  label="Address Line 2"
                  name="profile-address-2"
                  autoComplete="off"
                  fullWidth
                  value={formData.address2 || ''}
                  onChange={handleChange('address2')}
                  placeholder="Apt, Suite, Unit, etc. (optional)"
                />
              </Grid>
              {/* City */}
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  label="City"
                  name="profile-city"
                  autoComplete="off"
                  fullWidth
                  value={formData.city || ''}
                  onChange={handleChange('city')}
                />
              </Grid>
              {/* State/Province - wider to show full names */}
              <Grid size={{ xs: 12, sm: 6, md: 5 }}>
                {subdivisions.length > 0 ? (
                  <Autocomplete
                    options={subdivisions}
                    getOptionLabel={(option) => `${option.name} (${option.code})`}
                    value={selectedSubdivision}
                    onChange={(_, newValue) => {
                      setSelectedSubdivision(newValue);
                      setFormData({ ...formData, state: newValue?.code || newValue?.name || '' });
                    }}
                    renderInput={(params) => (
                      <TextField {...params} name="profile-state" autoComplete="off" label={subdivisionLabel} placeholder={`Select ${subdivisionLabel.toLowerCase()}...`} />
                    )}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    sx={{ minWidth: 200 }}
                  />
                ) : (
                  <TextField
                    label={subdivisionLabel}
                    name="profile-state"
                    autoComplete="off"
                    fullWidth
                    value={formData.state || ''}
                    onChange={handleChange('state')}
                    placeholder={`Enter ${subdivisionLabel.toLowerCase()}`}
                    helperText={selectedCountry ? undefined : 'Select a country first'}
                  />
                )}
              </Grid>
              {/* ZIP/Postal Code */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  label="ZIP/Postal Code"
                  name="profile-postal-code"
                  autoComplete="off"
                  fullWidth
                  value={formData.zipCode || ''}
                  onChange={handleChange('zipCode')}
                  placeholder="12345"
                />
              </Grid>
              {/* Country - wider to show full country names */}
              <Grid size={{ xs: 12, md: 8 }}>
                <Autocomplete
                  options={COUNTRIES}
                  getOptionLabel={(option) => option.name}
                  value={selectedCountry}
                  onChange={(_, newValue) => {
                    setSelectedCountry(newValue);
                    setSelectedSubdivision(null); // Reset subdivision when country changes
                    setFormData({ 
                      ...formData, 
                      country: newValue?.name || '',
                      state: '' // Clear state when country changes
                    });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Country" placeholder="Start typing to search..." />
                  )}
                  isOptionEqualToValue={(option, value) => option.code === value.code}
                  sx={{ minWidth: 280 }}
                />
              </Grid>
            </Grid>

            {/* Section save button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save Address'}
              </Button>
            </Box>
          </CollapsibleSettingsSection>

          {!isMinor && (
          <>
          <Divider sx={{ my: 3 }} />

          <CollapsibleSettingsSection
            sectionId="credential-management"
            expanded={isExpanded('credential-management')}
            onToggle={() => toggleSection('credential-management')}
            title={(
              <Typography variant="h6" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BadgeIcon />
                Credential Management
              </Typography>
            )}
          >
            {credentialError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCredentialError(null)}>
                {credentialError}
              </Alert>
            )}

            {credentialSuccess && (
              <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCredentialSuccess(null)}>
                {credentialSuccess}
              </Alert>
            )}

            {credentialsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress size={24} />
              </Box>
            ) : credentials.length === 0 ? (
              <Alert severity="info">
                You don't have any credentials yet. Visit the Credentialing Wizard to add vendor, marketer, or developer credentials.
              </Alert>
            ) : isBusinessProfile ? (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    This business account currently has the following issued credentials:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    {Array.from(new Set(credentials.map((credential) => credential.credentialType))).map((credentialType) => (
                      <Chip key={credentialType} label={credentialType} color="primary" variant="outlined" />
                    ))}
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<SendIcon />}
                    href="https://register.qixit.com/register"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open Credentialing Wizard to Add More
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Stack spacing={2}>
                {credentials.map((credential) => {
                  const edits = credentialEdits[credential.credentialId];
                  const showBusinessFields = credential.isBusinessAccount
                    || !!credential.businessName || !!credential.fein || !!credential.websiteUrl;
                  const dirty = isCredentialProfileDirty(credential);
                  const saving = credentialSaving[credential.credentialId] || false;

                  return (
                    <Card key={credential.credentialId} variant="outlined">
                      <CardContent sx={{ pb: '16px !important' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                              {credential.credentialType}
                              {credential.isBusinessAccount && (
                                <Chip label="Business" size="small" sx={{ ml: 1 }} />
                              )}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Attached to: <strong>{credential.attachedUsername}</strong>
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              Created: {fmtDate(credential.createdDate)}
                            </Typography>
                          </Box>
                          {!credential.isBusinessAccount && availableUsernames.length > 1 && (
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<SwapHorizIcon />}
                              onClick={() => handleOpenTransferDialog(credential)}
                            >
                              Transfer
                            </Button>
                          )}
                        </Box>

                        {showBusinessFields && edits && (
                          <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                            <Grid container spacing={2}>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label="Business Name"
                                  value={edits.businessName}
                                  onChange={e => handleCredentialFieldChange(credential.credentialId, 'businessName', e.target.value)}
                                  fullWidth
                                  size="small"
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label="Tax ID (FEIN)"
                                  value={edits.fein}
                                  onChange={e => handleCredentialFieldChange(credential.credentialId, 'fein', e.target.value)}
                                  fullWidth
                                  size="small"
                                  helperText="Federal Employer Identification Number"
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <TextField
                                  label="Website URL"
                                  value={edits.websiteUrl}
                                  onChange={e => handleCredentialFieldChange(credential.credentialId, 'websiteUrl', e.target.value)}
                                  fullWidth
                                  size="small"
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                <Button
                                  variant="contained"
                                  size="small"
                                  disabled={!dirty || saving}
                                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                                  onClick={() => handleSaveCredentialProfile(credential.credentialId)}
                                >
                                  {saving ? 'Saving...' : 'Save'}
                                </Button>
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            )}

            {!isBusinessProfile && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Personal credentials (Vendor, Marketer, Developer) can be transferred between your consumer account and premium usernames.
                Business credentials cannot be transferred as they have their own separate mailbox.
              </Typography>
            )}
          </CollapsibleSettingsSection>

          <Divider sx={{ my: 3 }} />
          </>
          )}

          {/* Premium usernames */}
          {storeMode ? (
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>Premium Usernames</Typography>
              <Typography variant="body2" color="text.secondary">
                Premium username management is available on the Qixit website.
              </Typography>
            </Box>
          ) : (
            <PremiumUsernameSettings />
          )}

          {!isMinor && !storeMode && <RedemptionEligibilityPanel businessJointSession={businessJointSession} />}

          {!isMinor && !storeMode && <SpCommissionReportsPanel />}

          <Divider sx={{ my: 3 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" color="error" gutterBottom>
              Delete account
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Permanently close your Qixit account. You will receive a verification code by email to confirm.
              Remaining balance is forfeited at deletion.
            </Typography>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setClosureDialogOpen(true)}
            >
              Delete account
            </Button>
          </Box>

          {/* Save Button */}
          <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider', display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Email Change Dialog */}
      <Dialog open={emailDialogOpen} onClose={handleCloseEmailDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {emailChangeStep === 'enter' ? 'Change Email Address' : 'Verify New Email'}
        </DialogTitle>
        <DialogContent>
          {emailChangeError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setEmailChangeError(null)}>
              {emailChangeError}
            </Alert>
          )}
          {emailChangeSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {emailChangeSuccess}
            </Alert>
          )}

          {emailChangeStep === 'enter' ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter your new email address. A verification code will be sent to confirm the change.
              </Typography>
              <TextField
                label="Current Email"
                fullWidth
                value={personalAccount?.email || ''}
                disabled
                sx={{ mb: 2 }}
              />
              <TextField
                label="New Email Address"
                fullWidth
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="your.new@email.com"
                autoFocus
              />
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                A 6-digit verification code has been sent to <strong>{newEmail}</strong>. 
                Enter the code below to confirm your email change.
              </Typography>
              <TextField
                label="Verification Code"
                fullWidth
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                inputProps={{ maxLength: 6 }}
                autoFocus
                helperText="Enter the 6-digit code sent to your new email"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmailDialog} disabled={emailChangeLoading}>
            Cancel
          </Button>
          {emailChangeStep === 'enter' ? (
            <Button
              variant="contained"
              onClick={handleRequestEmailChange}
              disabled={emailChangeLoading || !newEmail.includes('@')}
              startIcon={emailChangeLoading ? <CircularProgress size={20} /> : null}
            >
              {emailChangeLoading ? 'Sending...' : 'Send Verification Code'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleConfirmEmailChange}
              disabled={emailChangeLoading || verificationCode.length !== 6}
              startIcon={emailChangeLoading ? <CircularProgress size={20} /> : null}
            >
              {emailChangeLoading ? 'Verifying...' : 'Confirm Email Change'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Business Email Change Dialog */}
      <Dialog open={businessEmailDialogOpen} onClose={handleCloseBusinessEmailDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {businessEmailChangeStep === 'enter' ? 'Change Business Email' : 'Verify New Business Email'}
        </DialogTitle>
        <DialogContent>
          {businessEmailChangeError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setBusinessEmailChangeError(null)}>
              {businessEmailChangeError}
            </Alert>
          )}
          {businessEmailChangeSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {businessEmailChangeSuccess}
            </Alert>
          )}

          {businessEmailChangeStep === 'enter' ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Enter the new business email address. A verification code will be sent to confirm the change.
              </Typography>
              <TextField
                label="Current Business Email"
                fullWidth
                value={businessProfileEdits.email || ''}
                disabled
                sx={{ mb: 2 }}
              />
              <TextField
                label="New Business Email"
                fullWidth
                type="email"
                value={businessNewEmail}
                onChange={(e) => setBusinessNewEmail(e.target.value)}
                placeholder="business@company.com"
                autoFocus
              />
            </>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                A 6-digit verification code has been sent to <strong>{businessNewEmail}</strong>.
                Enter the code below to confirm your business email change.
              </Typography>
              <TextField
                label="Verification Code"
                fullWidth
                value={businessEmailVerificationCode}
                onChange={(e) => setBusinessEmailVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                inputProps={{ maxLength: 6 }}
                autoFocus
                helperText="Enter the 6-digit code sent to your new business email"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBusinessEmailDialog} disabled={businessEmailChangeLoading}>
            Cancel
          </Button>
          {businessEmailChangeStep === 'enter' ? (
            <Button
              variant="contained"
              onClick={handleRequestBusinessEmailChange}
              disabled={businessEmailChangeLoading || !businessNewEmail.includes('@')}
              startIcon={businessEmailChangeLoading ? <CircularProgress size={20} /> : null}
            >
              {businessEmailChangeLoading ? 'Sending...' : 'Send Verification Code'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleConfirmBusinessEmailChange}
              disabled={businessEmailChangeLoading || businessEmailVerificationCode.length !== 6}
              startIcon={businessEmailChangeLoading ? <CircularProgress size={20} /> : null}
            >
              {businessEmailChangeLoading ? 'Verifying...' : 'Confirm Email Change'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* 2FA Setup Dialog */}
      <Dialog open={twoFactorDialogOpen} onClose={handleClose2FADialog} maxWidth="sm" fullWidth>
        <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
        <DialogContent>
          {twoFactorError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTwoFactorError(null)}>
              {twoFactorError}
            </Alert>
          )}

          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Box
                component="img"
                src="/qixit-authenticator.png"
                alt="Qixit"
                sx={{ width: 88, height: 88, objectFit: 'contain' }}
              />
              <Typography variant="subtitle1" sx={{ mt: 1, fontWeight: 600 }}>
                Qixit Authenticator
              </Typography>
            </Box>
            {nativeShell && manualKey && (
              <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
                On this phone, open Google Authenticator or Microsoft Authenticator, choose{' '}
                <strong>Enter a setup key</strong>, and paste the key below. You cannot scan a QR code from this
                same screen.
              </Alert>
            )}

            {manualKey && nativeShell && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Setup key (paste into your authenticator app):
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: 'monospace',
                    bgcolor: 'grey.100',
                    p: 1,
                    borderRadius: 1,
                    letterSpacing: 1,
                    wordBreak: 'break-all',
                  }}
                >
                  {manualKey}
                </Typography>
                <Button
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  sx={{ mt: 1 }}
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(manualKey);
                      setManualKeyCopied(true);
                      setTimeout(() => setManualKeyCopied(false), 2000);
                    } catch {
                      setTwoFactorError('Could not copy — select and copy the key manually.');
                    }
                  }}
                >
                  {manualKeyCopied ? 'Copied' : 'Copy setup key'}
                </Button>
              </Box>
            )}

            {qrCodeData && !nativeShell && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Scan this QR code with your authenticator app:
                </Typography>
                <img src={qrCodeData} alt="2FA QR Code" style={{ maxWidth: 200 }} />
              </Box>
            )}

            {manualKey && !nativeShell && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Or enter this code manually:
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontFamily: 'monospace',
                    bgcolor: 'grey.100',
                    p: 1,
                    borderRadius: 1,
                    letterSpacing: 1,
                  }}
                >
                  {manualKey}
                </Typography>
              </Box>
            )}

            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Enter verification code"
                value={totpVerificationCode}
                onChange={(e) => setTotpVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                helperText="Enter the 6-digit code from your authenticator app"
                inputProps={{ maxLength: 6 }}
                autoFocus
              />
            </Box>

            {backupCodes.length > 0 && (
              <Alert severity="warning" sx={{ mb: 3, textAlign: 'left' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Save these backup codes!</strong> You can use them if you lose access to your authenticator app.
                </Typography>
                <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                  {backupCodes.map((code, i) => (
                    <div key={i}>{code}</div>
                  ))}
                </Box>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose2FADialog} disabled={twoFactorLoading}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleVerify2FA} 
            disabled={twoFactorLoading || totpVerificationCode.length !== 6}
            startIcon={twoFactorLoading ? <CircularProgress size={20} /> : null}
          >
            {twoFactorLoading ? 'Verifying...' : 'Verify & Enable'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disable 2FA Dialog */}
      <Dialog open={disableDialogOpen} onClose={() => setDisableDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
        <DialogContent>
          {twoFactorError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setTwoFactorError(null)}>
              {twoFactorError}
            </Alert>
          )}

          <Alert severity="warning" sx={{ mb: 2 }}>
            Disabling two-factor authentication will make your account less secure.
          </Alert>

          <Alert severity="info" icon={<QrCodeIcon />} sx={{ mb: 2 }}>
            <strong>Open your authenticator app</strong> (Google Authenticator, Authy, etc.) and enter
            the current 6-digit code shown for your Qixit account. No code will be sent to you —
            the code refreshes every 30 seconds in the app.
          </Alert>

          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Lost your authenticator?</strong> Use a backup code from setup, or confirm with an
            enrolled passkey below (a fresh passkey prompt — not just being signed in).
          </Alert>

          <TextField
            fullWidth
            label="6-digit authenticator code or backup code"
            value={disableCode}
            onChange={(e) => setDisableCode(e.target.value.replace(/[^0-9a-zA-Z-]/g, '').slice(0, 12))}
            inputProps={{ maxLength: 12 }}
            helperText="Enter the current code from your authenticator app, or an 8-digit backup code from setup"
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
          <Button onClick={() => { setDisableDialogOpen(false); setDisableCode(''); }} disabled={twoFactorLoading}>
            Cancel
          </Button>
          {passkeyCount > 0 && (
            <Button
              variant="outlined"
              onClick={() => void handleDisable2FAWithPasskey()}
              disabled={twoFactorLoading}
              startIcon={twoFactorLoading ? <CircularProgress size={18} /> : <FingerprintIcon />}
            >
              Confirm with passkey
            </Button>
          )}
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleDisable2FA()}
            disabled={twoFactorLoading || disableCode.length < 6}
            startIcon={twoFactorLoading ? <CircularProgress size={20} /> : null}
          >
            {twoFactorLoading ? 'Disabling...' : 'Disable with code'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Registered passkeys list */}
      <Dialog open={passkeysListDialogOpen} onClose={() => setPasskeysListDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Registered passkeys</DialogTitle>
        <DialogContent dividers>
          {passkeyError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPasskeyError(null)}>
              {passkeyError}
            </Alert>
          )}
          {passkeysListLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : passkeysList.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No passkeys are registered for this account.
            </Typography>
          ) : (
            <List disablePadding>
              {passkeysList.map((pk) => (
                <ListItem
                  key={pk.passkeyId}
                  alignItems="flex-start"
                  sx={{ px: 0 }}
                  secondaryAction={
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.25 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                        Remove key with
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        {passkeyConfirmCapabilities.showPc && (
                          <Tooltip title="Confirm with this computer / password manager, then remove this passkey">
                            <span>
                              <Button
                                size="small"
                                color="error"
                                disabled={passkeyLoading}
                                onClick={() => void handleRemoveOnePasskey(pk.passkeyId, 'platform')}
                              >
                                PC
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        {passkeyConfirmCapabilities.showPhone && (
                          <Tooltip title="Confirm with phone QR, then remove this passkey">
                            <span>
                              <Button
                                size="small"
                                color="error"
                                disabled={passkeyLoading}
                                onClick={() => void handleRemoveOnePasskey(pk.passkeyId, 'hybrid')}
                              >
                                Phone
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                    <FingerprintIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={pk.type || pk.label}
                    secondary={
                      <>
                        {pk.label && pk.label !== pk.type && (
                          <Typography component="span" variant="body2" color="text.secondary" display="block">
                            {pk.label}
                          </Typography>
                        )}
                        {pk.transports && (
                          <Typography component="span" variant="body2" color="text.secondary" display="block">
                            Transports: {pk.transports}
                          </Typography>
                        )}
                        <Typography component="span" variant="caption" color="text.secondary" display="block">
                          Added {fmtDate(pk.createdUtc)}
                          {pk.lastUsedUtc ? ` · Last used ${fmtDate(pk.lastUsedUtc)}` : ' · Not used yet'}
                        </Typography>
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
          {twoFactorEnabled && passkeysList.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 1.5 }}>
                Google/Microsoft password-manager keys often cannot complete Windows phone/QR confirm.
                Use your authenticator app code to remove a key if passkey confirm fails.
              </Alert>
              <TextField
                fullWidth
                size="small"
                label="Authenticator or backup code"
                value={removeOneCode}
                onChange={(e) => setRemoveOneCode(e.target.value.trim())}
                sx={{ mb: 1 }}
              />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {passkeysList.map((pk) => (
                  <Button
                    key={`code-remove-${pk.passkeyId}`}
                    size="small"
                    color="error"
                    variant="outlined"
                    disabled={passkeyLoading || removeOneCode.length < 6}
                    onClick={() => {
                      setRemoveOneTargetId(pk.passkeyId);
                      void handleRemoveOnePasskeyWithCode(pk.passkeyId);
                    }}
                  >
                    {passkeyLoading && removeOneTargetId === pk.passkeyId
                      ? 'Removing…'
                      : `Remove with code${passkeysList.length > 1 ? ` (${(pk.label || pk.type || 'key').slice(0, 24)})` : ''}`}
                  </Button>
                ))}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasskeysListDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Three portable-first options — password-manager-only MFA is discouraged as sole factor */}
      <Dialog open={registerPasskeyDialogOpen} onClose={() => setRegisterPasskeyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add a passkey</DialogTitle>
        <DialogContent>
          {passkeysListLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {!passkeysListLoading && passkeyAddOptions.existingSummary.length > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Already registered:{' '}
              <strong>{passkeyAddOptions.existingSummary.join('; ')}</strong>.
            </Alert>
          )}
          {platformAuthenticatorAvailable === false && passkeyAddOptions.windowsHello.available && (
            <Alert severity="info" sx={{ mb: 2 }}>
              This browser reports no platform authenticator. Windows Hello may need a PIN (Settings → Accounts →
              Sign-in options). Prefer Password manager, Phone, or Security key if Hello is unavailable.
            </Alert>
          )}
          <Stack spacing={1.5}>
            <Box>
              <Button
                fullWidth
                variant="contained"
                onClick={() => void handleRegisterPasskey('platform')}
                startIcon={<FingerprintIcon />}
                disabled={!passkeyAddOptions.windowsHello.available || passkeysListLoading}
              >
                Windows Hello (this device)
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {passkeyAddOptions.windowsHello.note}
              </Typography>
            </Box>
            <Box>
              <Button
                fullWidth
                variant="contained"
                color="secondary"
                onClick={() => void handleRegisterPasskey('any')}
                startIcon={<FingerprintIcon />}
                disabled={!passkeyAddOptions.passwordManager.available || passkeysListLoading}
              >
                Password manager (Google or Microsoft)
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {passkeyAddOptions.passwordManager.note}
              </Typography>
            </Box>
            <Box>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => void handleRegisterPasskey('hybrid')}
                startIcon={<PhoneIcon />}
                disabled={passkeysListLoading}
              >
                Phone or tablet (QR code)
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {passkeyAddOptions.hybrid.note}
              </Typography>
            </Box>
            <Box>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => void handleRegisterPasskey('security-key')}
                startIcon={<BadgeIcon />}
                disabled={passkeysListLoading}
              >
                Security key (USB / NFC)
              </Button>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                {passkeyAddOptions.securityKey.note}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
          {passkeyCount > 0 && (
            <Button
              color="error"
              onClick={() => {
                setRegisterPasskeyDialogOpen(false);
                setRemovePasskeysDialogOpen(true);
              }}
            >
              Remove existing passkeys…
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setRegisterPasskeyDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Remove All Passkeys Confirmation */}
      <Dialog
        open={removePasskeysDialogOpen}
        onClose={() => {
          if (!passkeyLoading) {
            setRemovePasskeysDialogOpen(false);
            setRemovePasskeysCode('');
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FingerprintIcon color="error" />
          Remove All Passkeys
        </DialogTitle>
        <DialogContent>
          {passkeyError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPasskeyError(null)}>
              {passkeyError}
            </Alert>
          )}
          <Alert severity="warning" sx={{ mb: 2 }}>
            This removes every active passkey from your Qixit account (including duplicates we stored). Sign-in
            with those keys will stop working immediately.
          </Alert>
          <Alert severity="info" sx={{ mb: 2 }}>
            Confirm with a <strong>fresh</strong> this-computer or phone passkey prompt (not just being signed
            in). Choose Phone if you want to use a QR / phone passkey. If no working passkey remains, use your
            authenticator app or a backup code below.
          </Alert>
          <Alert severity="info" sx={{ mb: 2 }}>
            Windows / Google / Microsoft may still show old entries for localhost — those live in the
            OS or password manager. Delete them under Windows Settings → Passkeys (or your password manager)
            if you want a clean Hello / passkey reset. We cannot erase those copies from the browser.
          </Alert>
          <Typography variant="body2" sx={{ mb: 2 }}>
            You currently have <strong>{passkeyCount}</strong> passkey{passkeyCount !== 1 ? 's' : ''} registered
            on this account.
          </Typography>
          {twoFactorEnabled && (
            <TextField
              fullWidth
              label="Authenticator or backup code (if passkey unavailable)"
              value={removePasskeysCode}
              onChange={(e) =>
                setRemovePasskeysCode(e.target.value.replace(/[^0-9a-zA-Z-]/g, '').slice(0, 12))
              }
              inputProps={{ maxLength: 12 }}
              helperText="Use when you cannot complete a passkey prompt"
              disabled={passkeyLoading}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, px: 3, pb: 2 }}>
          <Button
            onClick={() => {
              setRemovePasskeysDialogOpen(false);
              setRemovePasskeysCode('');
            }}
            disabled={passkeyLoading}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={passkeyLoading ? <CircularProgress size={18} /> : <FingerprintIcon />}
            onClick={() =>
              void handleRemoveAllPasskeysWithPasskey(
                passkeyConfirmCapabilities.onlySyncedPasswordManagers ? 'any' : 'platform'
              )
            }
            disabled={passkeyLoading || passkeyCount < 1 || !passkeyConfirmCapabilities.showPc}
          >
            Confirm with this computer
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={passkeyLoading ? <CircularProgress size={18} /> : <PhoneIcon />}
            onClick={() =>
              void handleRemoveAllPasskeysWithPasskey(
                passkeyConfirmCapabilities.onlySyncedPasswordManagers ? 'any' : 'hybrid'
              )
            }
            disabled={passkeyLoading || passkeyCount < 1 || !passkeyConfirmCapabilities.showPhone}
          >
            Confirm with phone
          </Button>
          {twoFactorEnabled && (
            <Button
              variant="contained"
              color="error"
              startIcon={passkeyLoading ? <CircularProgress size={18} /> : <DeleteIcon />}
              onClick={() => void handleRemoveAllPasskeysWithCode()}
              disabled={passkeyLoading || removePasskeysCode.length < 6}
            >
              Remove with code
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Phone Verification Dialog */}
      <Dialog open={phoneDialogOpen} onClose={() => setPhoneDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{phoneVerifyTarget === 'business' ? 'Verify Business Phone' : 'Verify Phone Number'}</DialogTitle>
        <DialogContent>
          {phoneVerifyError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPhoneVerifyError(null)}>
              {phoneVerifyError}
            </Alert>
          )}
          {!phoneCodeSent ? (
            <Box>
              {phoneVerifyTarget === 'personal' && savedPhoneNumber !== (formData.phoneNumber || '').trim() && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Your phone number was changed. Please verify the new number to keep your account secure.
                </Alert>
              )}
              {phoneVerifyTarget === 'business' && businessPhoneChangedUnsaved && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  The business phone number was changed. Verify the new number after saving, or verify the number shown below.
                </Alert>
              )}
              <Typography variant="body2" paragraph>
                Enter your phone number to receive a verification code via SMS. Include the country code (e.g., +1 for US).
              </Typography>
              <TextField
                fullWidth
                label="Phone Number"
                type="tel"
                value={phoneValue}
                onChange={(e) => setPhoneValue(e.target.value)}
                margin="normal"
                autoFocus
                placeholder="+12125551234"
                helperText="Format: +[country code][number]"
              />
            </Box>
          ) : (
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                A verification code was sent to <strong>{phoneValue}</strong>
              </Alert>
              <TextField
                fullWidth
                label="Verification Code"
                value={phoneCode}
                onChange={(e) => setPhoneCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                margin="normal"
                autoFocus
                inputProps={{ maxLength: 6 }}
                helperText="Enter the 6-digit code"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhoneDialogOpen(false)}>Cancel</Button>
          {!phoneCodeSent ? (
            <Button
              variant="contained"
              onClick={handleSendPhoneCode}
              disabled={phoneSending || !phoneValue}
              startIcon={phoneSending ? <CircularProgress size={20} /> : <SendIcon />}
            >
              {phoneSending ? 'Sending…' : 'Send Code'}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleVerifyPhoneCode}
              disabled={phoneVerifying || phoneCode.length < 6}
              startIcon={phoneVerifying ? <CircularProgress size={20} /> : <CheckCircleIcon />}
            >
              {phoneVerifying ? 'Verifying…' : 'Verify'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Address Verification Dialog */}
      <Dialog open={addressDialogOpen} onClose={() => setAddressDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{addressVerifyTarget === 'business' ? 'Verify Business Address' : 'Verify Physical Address'}</DialogTitle>
        <DialogContent>
          {addressVerifyError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAddressVerifyError(null)}>
              {addressVerifyError}
            </Alert>
          )}
          {!addressResult ? (
            <Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                {addressVerifyTarget === 'business'
                  ? 'Verify the business mailing address via USPS. The standardized address will be saved to the business credential.'
                  : 'Verify your address via USPS. The address will be standardized and saved to your account.'}
              </Typography>
              <TextField
                fullWidth label="Street Address" value={addressForm.street1}
                onChange={(e) => setAddressForm({ ...addressForm, street1: e.target.value })}
                margin="normal" autoFocus placeholder="123 Main St"
              />
              <TextField
                fullWidth label="Apt / Suite (optional)" value={addressForm.street2}
                onChange={(e) => setAddressForm({ ...addressForm, street2: e.target.value })}
                margin="normal" placeholder="Apt 4B"
              />
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField fullWidth label="City" value={addressForm.city}
                    onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })} margin="normal" />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <TextField fullWidth label="State" value={addressForm.state}
                    onChange={(e) => setAddressForm({ ...addressForm, state: e.target.value.toUpperCase().slice(0, 2) })}
                    margin="normal" placeholder="IL" inputProps={{ maxLength: 2 }} />
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <TextField fullWidth label="ZIP" value={addressForm.zip}
                    onChange={(e) => setAddressForm({ ...addressForm, zip: e.target.value.slice(0, 5) })}
                    margin="normal" placeholder="60601" inputProps={{ maxLength: 5 }} />
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>{addressResult.message}</Alert>
              {addressResult.wasCorrected && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Your address was standardized by USPS. The corrected address below will be saved to your account.
                </Alert>
              )}
              <Card variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Verified Address:</Typography>
                <Typography>{addressResult.verifiedAddress?.street1}</Typography>
                {addressResult.verifiedAddress?.street2 && <Typography>{addressResult.verifiedAddress.street2}</Typography>}
                <Typography>
                  {addressResult.verifiedAddress?.city}, {addressResult.verifiedAddress?.state}{' '}
                  {addressResult.verifiedAddress?.zip}
                </Typography>
              </Card>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddressDialogOpen(false)}>Cancel</Button>
          {!addressResult ? (
            <Button
              variant="contained"
              onClick={handleAddressSubmit}
              disabled={addressSubmitting || !addressForm.street1 || !addressForm.city || !addressForm.state || !addressForm.zip}
              startIcon={addressSubmitting ? <CircularProgress size={20} /> : <HomeIcon />}
            >
              {addressSubmitting ? 'Verifying…' : 'Verify Address'}
            </Button>
          ) : (
            <Button variant="contained" color="success" onClick={handleAddressConfirm} startIcon={<CheckCircleIcon />}>
              Confirm &amp; Save
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Identity Verification confirmation */}
      <Dialog
        open={idVerificationConfirmOpen}
        onClose={() => !idVerificationStarting && setIdVerificationConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {verificationStatus?.isIdVerified ? 'Re-verify Identity' : 'Verify Identity'}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Submitting this verification will charge ${ID_VERIFICATION_FEE.toFixed(2)} from your Qixit balance.
          </Alert>
          <Typography variant="body2" color="text.secondary">
            If your available balance is too low, you will get a balance-required
            message before verification starts.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setIdVerificationConfirmOpen(false)}
            disabled={idVerificationStarting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={idVerificationStarting}
            startIcon={idVerificationStarting ? <CircularProgress size={16} color="inherit" /> : <BadgeIcon />}
            onClick={async () => {
              setIdVerificationConfirmOpen(false);
              await handleStartIdentityVerification();
            }}
          >
            {idVerificationStarting ? 'Starting…' : `Continue (${ID_VERIFICATION_FEE.toFixed(2)} fee)`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={idPreviewOpen}
        onClose={() => setIdPreviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>My Verification Preview</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {idPreviewLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!idPreviewLoading && idPreviewError && (
              <Alert severity="error">{idPreviewError}</Alert>
            )}
            {!idPreviewLoading && !idPreviewError && idPreviewEvidence && (
              <>
                <Alert severity="info">
                  This is the same verification evidence view requesters receive after approval.
                </Alert>
                <Alert severity="warning">
                  {previewMailingAddress
                    ? (
                      <>
                        Your ID-confirmed mailing address (<strong>{previewMailingAddress}</strong>) is never shared unless you explicitly approve a separate request to verify your mailing address.
                      </>
                    )
                    : 'Your ID-confirmed mailing address is never shared unless you explicitly approve a separate request to verify your mailing address.'}
                </Alert>
                {idPreviewEvidence.verifiedFullName && (
                  <Typography variant="body2">
                    Verified name: <strong>{idPreviewEvidence.verifiedFullName}</strong>
                  </Typography>
                )}
                {(idPreviewEvidence.idState || idPreviewEvidence.idCountry) && (
                  <Typography variant="body2">
                    ID location: <strong>{[idPreviewEvidence.idState, idPreviewEvidence.idCountry].filter(Boolean).join(', ')}</strong>
                  </Typography>
                )}
                {idPreviewEvidence.portraitImageUrl && (
                  <Box>
                    <Typography variant="subtitle2">ID portrait</Typography>
                    <Box component="img" src={idPreviewEvidence.portraitImageUrl} alt="ID portrait" sx={{ mt: 1, maxWidth: 300, width: '100%', borderRadius: 1 }} />
                  </Box>
                )}
                {idPreviewEvidence.livenessVideoUrl && (
                  <Box>
                    <Typography variant="subtitle2">Didit live capture</Typography>
                    <Box component="video" controls src={idPreviewEvidence.livenessVideoUrl} sx={{ mt: 1, maxWidth: 340, width: '100%', borderRadius: 1 }} />
                  </Box>
                )}
                {!idPreviewEvidence.portraitImageUrl && !idPreviewEvidence.livenessVideoUrl && (
                  <Alert severity="info">
                    ID portrait and live capture are not available yet for the latest persisted verification evidence.
                  </Alert>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIdPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Credential Dialog */}
      <Dialog open={transferDialogOpen} onClose={() => setTransferDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Transfer Credential</DialogTitle>
        <DialogContent>
          {credentialError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCredentialError(null)}>
              {credentialError}
            </Alert>
          )}

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Transfer your <strong>{selectedCredential?.credentialType}</strong> credential to a different username.
            This determines which username appears on your products, campaigns, or developer profile.
          </Typography>

          <Alert severity="info" sx={{ mb: 2 }}>
            Currently attached to: <strong>{selectedCredential?.attachedUsername}</strong>
          </Alert>

          <FormControl fullWidth>
            <InputLabel>Transfer to</InputLabel>
            <Select
              value={transferTargetUsername}
              label="Transfer to"
              onChange={(e) => setTransferTargetUsername(e.target.value)}
            >
              {availableUsernames
                .filter(u => u !== selectedCredential?.attachedUsername)
                .map((username) => (
                  <MenuItem key={username} value={username}>
                    {username}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferDialogOpen(false)} disabled={transferLoading}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleTransferCredential} 
            disabled={transferLoading || !transferTargetUsername}
            startIcon={transferLoading ? <CircularProgress size={20} /> : <SwapHorizIcon />}
          >
            {transferLoading ? 'Transferring...' : 'Transfer Credential'}
          </Button>
        </DialogActions>
      </Dialog>

      <AccountClosureDialog
        open={closureDialogOpen}
        onClose={() => setClosureDialogOpen(false)}
        onClosureComplete={async () => {
          setClosureDialogOpen(false);
          await logout();
        }}
        currentBalance={closureBalance}
      />

      {/* Phase 3 — Local Archive section */}
      <Divider sx={{ my: 3 }} />
      <LocalArchivePanel />
    </>
  );
};

export default AccountSettings;

